/**
 * PuzzleRoyaleBattle route
 *
 * The live N-player race screen. Structure mirrors the 1v1 BattleScreen
 * (countdown overlay, secondsLeft ticker, board sizing, idle guard) but the
 * right rail is the N-player RoyaleLeaderboardStrip and the left column shows a
 * single-row RoyaleStrip (points/combo/rank/who-to-play/clock) instead of the
 * 1v1 ScorePanel; all state comes from funChallengesRoyaleStore.
 *
 * Layout note: the root is a plain View (NOT a ScrollView). react-chessboard
 * derives drop squares from the board's screen rect, and a parent ScrollView
 * shifts pointer coords mid-drag → pieces land on the wrong square. The right
 * rail has its OWN ScrollView so long rosters still scroll. (item 2)
 *
 * The puzzle-solving UI reuses the Position Trainer walker verbatim via the
 * registry (PuzzleRoyaleAdapter re-registers PositionTrainerAdapterView under
 * PUZZLE_ROYALE). Like Puzzle Dash, royale advances per-player and can outrun
 * the issued sequence, so we copy PDB's failed-puzzle recycle into the
 * adapterCoords memo.
 *
 * Scoring/combo/points are server-authoritative (Phase 5 broadcasts them);
 * this screen renders whatever the store holds. The wire layer is a stub
 * today (useRoyaleSocket), so onMove is a dev no-op and the screen is driven
 * by the store / a mock dispatcher during Phases 0-4.
 *
 * Receives changeClient/setChangeClient/authComplete from Application.tsx
 * exactly like FunChallengeBattle (threaded generically to every screen).
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import {useNavigation} from '@react-navigation/native';

import {Chess} from 'chess.js';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import CCText from '@/Atoms/CCText';
// TRIAL (2026-07-09): royale-wide board swap to the new chessground board used
// by PlayChess. This usage is the read-only pre-start waiting board.
// Legacy revert target: import ChessboardWithArrow from '@/Components/ChessboardWithArrow';
import ChessboardWithArrow from '@/Components/Chessboard';
import useChessboardColorTheme from '@/Hooks/ChessboardColorTheme/useChessboardColorTheme';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import CCTextInput from '@/Atoms/CCTextInput/CCTextInput';
import CCLoader from '@/Atoms/CCLoader';
// Shared app pager (Previous / 1 2 3 / Next), as used by the tournaments tab.
import Pagination from '@/Screens/Games/Pagination';
// Existing repo atom (react-tiny-popover based, same one InfoIcon uses) — a bare
// `title` prop does NOT work here: react-native-web only forwards an allowlist
// of props to the DOM and `title` is not on it, so the leaderboard's icon
// headings had no tooltip at all.
import Tooltip from '@/Atoms/Tooltip';
import SlideModal from '@/Atoms/SlideModal';
import {FontSizes} from '@/Utils/Dimensions';
import {resolveSetupPly} from '@/Utils/puzzleEncrypter';

import useFunChallengesRoyaleStore from '@/Store/funChallengesRoyaleStore';
import useRoyaleSocket from '@/Hooks/FunChallengesSocket/useRoyaleSocket';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {useSocketReconnect} from '@/Hooks/UseSocket';
import ConnectionModal from '@/Screens/PlayChess/components/ConnectionModal';
import ConnectionNotice from './components/ConnectionNotice';

import {getAdapter} from '@/Screens/FunChallenges/registry';
import '@/Screens/FunChallenges/adapters';
import {BattleType, type BattlePuzzle} from '@/Screens/FunChallenges/types';
import useBattlePuzzles from '@/Hooks/FunChallengesApi/useBattlePuzzles';
import useSavePuzzleRoyaleResult from '@/Hooks/FunChallengesApi/useSavePuzzleRoyaleResult';
import useCreateClassroomRoyaleApi from '@/Hooks/ClassroomInfoApi/useCreateClassroomRoyaleApi';
import useSoloPersonalBest, {
  useInvalidateSoloPersonalBest,
} from '@/Hooks/FunChallengesApi/useSoloPersonalBest';
import {useInvalidateSoloLeaderboard} from '@/Hooks/FunChallengesApi/useSoloLeaderboard';

import RoyaleLeaderboardStrip from './components/RoyaleLeaderboardStrip';
import SoloStatsHeader from './components/SoloStatsHeader';
import SoloLeaderboardPanel from './components/SoloLeaderboardPanel';
import RoyaleSpectateView from './components/RoyaleSpectateView';
import RoyalePuzzleGridPanel from './components/RoyalePuzzleGridPanel';
import RoyaleClockPill from './components/RoyaleClockPill';
import RoyaleResultModal from './components/RoyaleResultModal';
import RoyaleEndScreen from './components/RoyaleEndScreen';
import RoyaleShareSheet from './components/RoyaleShareSheet';
import RoyaleLobbyRoster from './components/RoyaleLobbyRoster';
import {SHOW_LEAVE_RACE} from './royaleConstants';
import {TOPIC_LABELS} from './royaleTopics';
import {msUntil, serverNowMs} from './util/serverClock';
import type {PlayerRoyaleState} from './royaleTypes';

const MIN_WIDE = 980;
// Server 3-2-1 countdown length. The server stamps `starts_at = serverNow +
// COUNTDOWN_MS` and rejects any move before it (COUNTDOWN_IN_PROGRESS). We drive
// the client countdown off a LOCAL duration anchor (not the absolute startsAt)
// so a skewed device clock can't finish early — see the countdown effect.
const COUNTDOWN_MS = 3000;
// Small extra cushion (ms) before we let the first move through, to cover
// server-processing + network so a move sent the instant the local countdown
// hits zero still lands after the server's starts_at. Independent of clock skew.
const COUNTDOWN_MOVE_CUSHION_MS = 300;
const BG_PAGE = '#FAF6EB';
// Arena-parity neutrals for the Players / Spectators / Details panels, so they
// match the tournament lobby's cards. Kept local (same as every other colour in
// this file) rather than importing the leaderboard theme module.
const ARENA_LINE = '#F0E9DA'; // hairline card border
const ARENA_MUTED = '#9A9184'; // uppercase section labels
const TEAL = '#2A9D8F';
const TEAL_DARK = '#1F6C63';
const BROWN = '#4D3F37';
const TEXT_DARK = '#262322';
const TEXT_MUTED = '#8A7E5F';
const CREAM3 = '#EDE4CC';
const STARTING_FEN =
  'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export default function PuzzleRoyaleBattle(props: {
  // Multi-tab / auth-gate state, threaded down from Application.tsx exactly
  // like FunChallengeBattle. Drives the shared ConnectionModal ("Activate
  // Gameplay Here" / "Reconnect") so a backgrounded or unauthenticated tab can
  // reclaim the socket rather than silently feeling stuck mid-race. (These were
  // previously dropped on the floor — the royale screen never rendered the
  // modal, so a displaced/unauthenticated tab looked frozen.)
  changeClient?: boolean;
  setChangeClient?: (val: boolean) => void;
  authComplete?: boolean;
}) {
  const {changeClient, setChangeClient, authComplete = true} = props;
  const {width: viewportWidth, height: viewportHeight} = useWindowDimensions();
  const isWide = viewportWidth >= MIN_WIDE;
  const {isMobileOrTab} = useMediaQuery();
  const navigation = useNavigation<any>();
  const reconnectSocket = useSocketReconnect();
  const chessboardColorTheme = useChessboardColorTheme();

  const status = useFunChallengesRoyaleStore(s => s.status);
  const session = useFunChallengesRoyaleStore(s => s.session);
  const lobby = useFunChallengesRoyaleStore(s => s.lobby);
  const players = useFunChallengesRoyaleStore(s => s.players);
  const leaderboard = useFunChallengesRoyaleStore(s => s.leaderboard);
  const joinOrder = useFunChallengesRoyaleStore(s => s.joinOrder);
  const myUserId = useFunChallengesRoyaleStore(s => s.myUserId) ?? '';
  const endResult = useFunChallengesRoyaleStore(s => s.endResult);
  const errorMessage = useFunChallengesRoyaleStore(s => s.errorMessage);
  const clearError = useFunChallengesRoyaleStore(s => s.clearError);
  const setHostIsPlayer = useFunChallengesRoyaleStore(s => s.setHostIsPlayer);
  // Web spectate: am I a spectator, and whom am I following?
  const isSpectator = useFunChallengesRoyaleStore(s => s.isSpectator);
  // True when this ended state was rebuilt from persisted results for review —
  // gates the results POST only (see the store field's doc comment).
  const isReviewSnapshot = useFunChallengesRoyaleStore(s => s.isReviewSnapshot);
  const spectating = useFunChallengesRoyaleStore(s => s.spectating);
  const setSpectating = useFunChallengesRoyaleStore(s => s.setSpectating);
  const liveBoards = useFunChallengesRoyaleStore(s => s.liveBoards);
  const spectators = useFunChallengesRoyaleStore(s => s.spectators);
  const classroomContext = useFunChallengesRoyaleStore(s => s.classroomContext);
  // Per-player response trails — ordered `<puzzleId>:<uci>:<0|1>` triples. This
  // is the single source for the left panel's 70-box grid: field[2] is the
  // correct/wrong bit and field[0] is the puzzle id (→ starting FEN for hover).
  const trails = useFunChallengesRoyaleStore(s => s.trails);
  // My own display name — a last-resort self-label so a spectator's own row in
  // the spectators list reads "You" / their name even if the roster entry's
  // name went missing (#5).
  const myUsername = useLoggedInUserStore(
    s =>
      s.userData?.username ||
      s.userData?.name ||
      s.userData?.first_name ||
      s.email ||
      '',
  );

  // SOLO Puzzle Royale: a 1-player race. Read the flag off the authoritative
  // session settings (falls back to the optimistic lobby settings before
  // fun_session_start lands). Drives the solo layout: no lobby/roster UI, the
  // right rail shows the race track only, and "leave race" reads as "end game".
  const isSolo = !!(
    (session?.settings && (session.settings as any).solo) ||
    (lobby?.settings && (lobby.settings as any).solo)
  );
  // The time control this solo game is running at — seeds the PB fetch + the
  // leaderboard's default board.
  const soloTimeControl =
    session?.settings?.timerSec ?? lobby?.settings?.timerSec ?? 90;
  // Personal best for THIS time control (drives the stats-header "best" values +
  // the race-track ghost). Only fetched for solo (enabled by a valid tc).
  const {pb: soloPb} = useSoloPersonalBest(isSolo ? soloTimeControl : 0);
  const invalidateSoloLeaderboard = useInvalidateSoloLeaderboard();
  const invalidateSoloPb = useInvalidateSoloPersonalBest();

  const {
    sendMove,
    startRace,
    probeSession,
    setHostRole,
    leaveLobby,
    forfeit,
    rematch,
    spectateMove,
  } = useRoyaleSocket();
  const {mutate: saveRoyaleResult} = useSavePuzzleRoyaleResult();
  const {mutate: registerClassroomRoyale} = useCreateClassroomRoyaleApi();
  // Classroom royale rooms already registered with Django from THIS tab (per
  // roomId), so we register the initial lobby + each rematch lobby exactly once.
  const registeredRoyaleRoomsRef = useRef<Set<string>>(new Set());

  // Persist the result once on session end. Every client fires it; the server
  // dedupes on session_id + (history_id, user_id). Guard with a ref so a
  // re-render after 'ended' doesn't re-POST.
  const savedRef = useRef(false);
  // Reset the guard whenever a NEW session begins (e.g. a rematch reuses this
  // mounted screen) so the next race's result still persists.
  const sessionId = session?.sessionId;
  useEffect(() => {
    savedRef.current = false;
  }, [sessionId]);

  // BASELINE personal-best, frozen at the START of this session. `soloPb`
  // refetches on game-end (to include the just-played game), so comparing the
  // live score against `soloPb` would make "new best" flip on a mere TIE and
  // would RESET the celebration once the game ends (best jumps up to equal your
  // final score). We snapshot the pre-game PB per session and compare against
  // THAT: "new best" = you beat what you walked in with, and it stays highlighted
  // after the game because the baseline never moves. Captured once per sessionId,
  // as soon as a PB value is available for this game.
  const soloBaselineRef = useRef<{
    sessionId: string | undefined;
    points: number;
    combo: number;
    solves: number;
    hasPb: boolean;
  } | null>(null);
  if (
    isSolo &&
    sessionId &&
    // Only freeze once the PB has actually LOADED (soloPb non-null) — capturing
    // while it's still null would lock in a premature 0/false baseline and make
    // the very first solve look like a new record wrongly.
    soloPb &&
    (!soloBaselineRef.current ||
      soloBaselineRef.current.sessionId !== sessionId)
  ) {
    soloBaselineRef.current = {
      sessionId,
      points: soloPb.bestPoints ?? 0,
      combo: soloPb.bestCombo ?? 0,
      solves: soloPb.bestScore ?? 0,
      hasPb: !!soloPb.hasPb,
    };
  }
  const soloBaseline = soloBaselineRef.current;

  // Latched once a solo game ends this session (survives a rematch) — gates the
  // solo leaderboard's refetch-on-focus. Declared here (before any early return)
  // to keep hook order stable; assigned later where `isEnded` is known.
  const soloEndedOnceRef = useRef(false);
  useEffect(() => {
    // Spectators never persist a result (they aren't ranked participants) — the
    // players' own clients POST it (server dedupes anyway).
    // isReviewSnapshot: this `ended` state was rebuilt from PERSISTED results so
    // a completed tournament could be reviewed — nothing was played here, so
    // POSTing would overwrite the real row with a synthetic one (end_reason
    // 'time_up', punish false, finished_at null, attempts[].uci '-'). Django
    // overwrites unconditionally, so without this guard merely OPENING a
    // finished tournament corrupted its standings, for every viewer.
    if (
      status !== 'ended' ||
      !endResult ||
      !session ||
      savedRef.current ||
      isSpectator ||
      isReviewSnapshot
    ) {
      return;
    }
    savedRef.current = true;
    const ranked = leaderboard.length
      ? leaderboard
      : Object.keys(players);
    const playerPayloads = ranked.map((id, idx) => {
      const p = players[id];
      // Per-puzzle attempt detail, parsed from a player's response trail — an
      // ordered list of `<puzzleId>:<uci>:<0|1>` triples. IMPORTANT: this client
      // only has the REAL uci for its OWN trail (from its sender echo); other
      // players' trails are synthesised (`<puzzleId>::<bit>`, blank uci) because
      // their move broadcasts don't carry `square`. Since EVERY client POSTs the
      // whole standings, sending another player's blank-uci attempts would let
      // this POST clobber that player's good row (last-writer-wins). So we fill
      // solved_ids/failed_ids/attempts ONLY for our OWN id and leave them EMPTY
      // for everyone else — each player's own client fills their own row, and
      // the Django POST refuses to overwrite a non-empty attempts with an empty
      // one (order-independent). Aggregates (points/score/wrong/rank) are
      // server-authoritative for all, so those stay accurate regardless.
      const isMe = String(id) === String(myUserId);
      const trail = isMe ? trails[id] ?? [] : [];
      const solvedIds: number[] = [];
      const failedIds: number[] = [];
      const attempts: Array<{puzzleId: number; uci: string; solved: boolean}> =
        [];
      for (const entry of trail) {
        const parts = String(entry).split(':');
        if (parts.length < 3) continue;
        const puzzleId = Number(parts[0]) || 0;
        const uci = parts[1] || '';
        const solved = parts[parts.length - 1] === '1';
        if (!puzzleId) continue;
        attempts.push({puzzleId, uci, solved});
        (solved ? solvedIds : failedIds).push(puzzleId);
      }
      return {
        user_id: Number(id) || 0,
        points: p?.points ?? 0,
        score: p?.solved ?? 0,
        wrong: p?.wrong ?? 0,
        best_combo: p?.bestCombo ?? 0,
        piece: p?.piece ?? ('pawn' as const),
        rank: idx + 1,
        accuracy:
          p && p.solved + p.wrong > 0 ? p.solved / (p.solved + p.wrong) : 0,
        solved_ids: solvedIds,
        failed_ids: failedIds,
        attempts,
        eliminated: !!p?.eliminated,
        finished_at: null,
      };
    });
    const tc = session.settings.timerSec;
    saveRoyaleResult(
      {
        session_id: session.sessionId,
        game_type: 'puzzle_royale',
        // SOLO: tells Django to accept the single-player payload (the >=2 guard is
        // relaxed on this flag). Harmless/false for multiplayer.
        solo: isSolo,
        reason: endResult.reason,
        end_reason: endResult.reason,
        puzzle_ids: (session.coords || []).map(c => Number(c)).filter(Boolean),
        time_control_sec: tc,
        topics: session.settings.topics || [],
        difficulty: session.settings.difficulty,
        punish: !!session.settings.punish,
        punish_threshold: session.settings.punishThreshold ?? null,
        players: playerPayloads,
      },
      isSolo
        ? {
            // FRESHNESS (SL-7): the moment the result is saved, refetch the solo
            // leaderboard + PB so this just-finished score shows immediately —
            // no waiting out the poll interval. The server also busts its Redis
            // key on write, so the refetch returns the fresh board. Own-game
            // only; other players refresh on the normal interval.
            onSuccess: () => {
              invalidateSoloLeaderboard(tc);
              invalidateSoloPb(tc);
            },
          }
        : undefined,
    );
  }, [
    status,
    endResult,
    session,
    leaderboard,
    players,
    trails,
    saveRoyaleResult,
    isSpectator,
    isReviewSnapshot,
    isSolo,
    invalidateSoloLeaderboard,
    invalidateSoloPb,
  ]);

  const me = myUserId ? players[myUserId] : undefined;
  const myIndex = me?.myIndex ?? 0;

  // ── Coach "watch" mode (classroom only) ───────────────────────────────────
  // In a classroom royale the host/coach can choose to WATCH instead of play.
  // Unlike the standalone spectate path (hostIsPlayer:false → the server drops
  // the host from playerIds, which would block a lone student from starting and
  // from saving a result), a watching coach stays a REAL wire-player
  // (hostIsPlayer:true) so the ≥2 start-gate + the results POST are satisfied
  // and the coach gets a (0-point) player row — but their board is rendered
  // read-only and auto-follows the leading student, and their own row is hidden
  // from the leaderboard/race strip. Purely client-side; no server/DB change.
  // `coachWatching` is only meaningful in a classroom context for the host.
  const [coachWatchPref, setCoachWatchPref] = useState(false);
  // Host id is resolved below (needs lobby/session); recompute a lightweight
  // host check here so coachWatching can gate render without forward refs.
  const hostIdEarly =
    lobby?.hostId || (session?.settings?.hostId as string) || '';
  const isHostEarly = !!hostIdEarly && String(hostIdEarly) === String(myUserId);
  // Pre-start (waiting room) the coach's Watch choice is only local
  // (coachWatchPref); once the race starts the server echoes the authoritative
  // watcher id on the session (session.watcherId), so ALL clients agree.
  const sessionWatcherId = session?.watcherId || '';
  const iAmWatcher =
    !!sessionWatcherId && String(sessionWatcherId) === String(myUserId);
  const coachWatching =
    // Live: I'm the server-confirmed watcher…
    iAmWatcher ||
    // …or pre-start local preference (classroom host chose Watch), before the
    // session/watcherId exists.
    (!!classroomContext && isHostEarly && coachWatchPref && !isSpectator && !session);
  // Anywhere the UI must be READ-ONLY / spectate-styled, use this: the server
  // spectator OR a watching classroom coach.
  const effectiveSpectator = isSpectator || coachWatching;

  // Hide the WATCHING COACH's (0-point) row from the leaderboard + race strip on
  // EVERY client (students included), so nobody sees a "coach" sitting at 0.
  // Server-authoritative via session.watcherId; falls back to my own id while I
  // watch pre-start. (The coach's server-side player row still exists — this is
  // display-only.)
  const hiddenRowId =
    sessionWatcherId || (coachWatching ? myUserId : '');
  const displayLeaderboard = useMemo(
    () =>
      hiddenRowId ? leaderboard.filter(id => id !== hiddenRowId) : leaderboard,
    [hiddenRowId, leaderboard],
  );
  const displayPlayers = useMemo(() => {
    if (!hiddenRowId) return players;
    const next: Record<string, PlayerRoyaleState> = {};
    for (const id of Object.keys(players)) {
      if (id !== hiddenRowId) next[id] = players[id];
    }
    return next;
  }, [hiddenRowId, players]);

  // Coach-watch: auto-follow the leading student once the race is live (the
  // coach never picked someone manually). Uses the coach-filtered leaderboard
  // so we never "follow" the coach themselves. The coach can still tap another
  // row to switch (selectable strip). Only sets a default when nothing is
  // followed yet or the followed id vanished.
  useEffect(() => {
    if (!coachWatching) return;
    const topStudent = displayLeaderboard[0] || null;
    if (!topStudent) return;
    if (!spectating || !players[spectating] || spectating === myUserId) {
      setSpectating(topStudent);
    }
  }, [coachWatching, displayLeaderboard, spectating, players, myUserId, setSpectating]);

  // Spectate: the followed player + whether they currently lead (King badge).
  const followed = spectating ? players[spectating] ?? null : null;
  const followedIsLeader = !!followed && leaderboard[0] === followed.userId;
  // Live leaderboard rank (1-based) for the strip's "current position" cell.
  const myRankLive = useMemo(() => {
    const i = leaderboard.findIndex(id => id === myUserId);
    return i >= 0 ? i + 1 : 0;
  }, [leaderboard, myUserId]);

  // Hydrate puzzle rows from the shared sequence (same fetch as PTB/PDB).
  // Memoised so the adapterCoords memo + the puzzles query don't see a new
  // array reference on every (200ms-ticking) re-render.
  const coords = useMemo(() => session?.coords ?? [], [session?.coords]);
  const {data: puzzlesData} = useBattlePuzzles(coords);
  const puzzles = useMemo(
    () => puzzlesData?.puzzles ?? {},
    [puzzlesData?.puzzles],
  );

  // ── Left-panel grid data (per-puzzle outcomes + FEN resolver) ─────────────
  // The panel shows MY progress, or the FOLLOWED player's when spectating. Both
  // come from the same `trails[playerId]` (ordered `<puzzleId>:<uci>:<0|1>`),
  // which hydrates on rejoin/spectate — so the followed player's grid is
  // accurate incl. history from before we started watching. Any triple missing
  // the bit renders as pending (grey) rather than a guessed colour.
  const gridOwnerId = effectiveSpectator ? spectating : myUserId;
  const gridOwner = gridOwnerId ? players[gridOwnerId] : undefined;
  const gridTrail = useMemo(
    () => (gridOwnerId ? trails[gridOwnerId] ?? [] : []),
    [trails, gridOwnerId],
  );
  // outcomes[i] = 'correct' | 'wrong' straight from the trail's bit (the store
  // keeps the trail in sync with every move — live AND on rejoin via
  // hydrateRoyale — so this is authoritative; no green-flood padding). The
  // current box is highlighted separately by the panel via currentIndex.
  const gridOutcomes = useMemo(() => {
    return gridTrail.map(triple => {
      const parts = triple.split(':');
      return parts[parts.length - 1] === '1' ? 'correct' : ('wrong' as const);
    }) as ('correct' | 'wrong')[];
  }, [gridTrail]);
  // Puzzle-id per issued index → the FEN the player actually SAW (#5): that's
  // the puzzle position AFTER the opponent's setup move (moves[0]) is pushed —
  // where it becomes the player's turn — NOT the raw puzzle.fen. Mirrors the
  // adapter's setup step. Prefer the trail's own puzzleId (what that player
  // saw); fall back to the shared coords sequence past the trail.
  const gridFenForIndex = useCallback(
    (index: number): string | undefined => {
      const fromTrail = gridTrail[index]?.split(':')[0];
      const puzzleId = fromTrail || coords[index];
      if (!puzzleId) return undefined;
      const puzzle = puzzles[puzzleId];
      if (!puzzle?.fen) return undefined;
      // no_setup (curated demo) puzzles have NO opponent setup ply — the FEN is
      // already the position the player saw, so return it as-is.
      if (puzzle.no_setup) return puzzle.fen;
      // Setup ply only — it is plaintext (drawn on the board), so this preview
      // never needs to touch the obfuscated answer.
      const setupUci = resolveSetupPly(puzzle);
      if (!setupUci || setupUci.length < 4) return puzzle.fen;
      try {
        const game = new Chess(puzzle.fen);
        game.move({
          from: setupUci.slice(0, 2),
          to: setupUci.slice(2, 4),
          promotion: 'q',
        });
        return game.fen();
      } catch {
        return puzzle.fen;
      }
    },
    [gridTrail, coords, puzzles],
  );
  const gridCurrentIndex = gridOwner?.myIndex ?? 0;
  // DURING the race the grid shows the whole pre-rolled sequence, so the player
  // sees what's still ahead of them. Once the race has ENDED there is nothing
  // ahead — rendering all 70 boxes for a 4-puzzle game just overflowed the
  // panel (the grid ran off the bottom of the page down to box 70). So post-game
  // the grid stops at what was actually attempted.
  // (Uses `status` directly, not the `isEnded` alias — that's declared further
  // down and would be a temporal-dead-zone crash here.)
  // +1 for the puzzle that was STILL ON SCREEN when the clock ran out. The trail
  // only records COMPLETED attempts, so a player who timed out mid-puzzle lost
  // that board from the review entirely (16 solved → exactly 16 boxes, and the
  // 17th they were actually looking at was gone). `myIndex` is `solved + wrong`,
  // i.e. the pointer to the in-flight puzzle, so include it when the sequence
  // actually has one — clamped to `coords.length` so a player who exhausted the
  // whole sequence doesn't gain a phantom box.
  const gridTotal =
    status === 'ended'
      ? Math.min(
          Math.max(gridTrail.length || gridOutcomes.length, gridCurrentIndex + 1),
          coords.length,
        )
      : coords.length;

  // Failed-puzzle recycle (copied from PDB) — royale advances per-player and
  // can outrun the issued sequence; recycle only this player's failed ids.
  const myResponses = useMemo(() => {
    // Reconstruct this player's response trail from… we don't keep it on the
    // store player row directly; recycle is a Phase-5 refinement once the
    // server echoes `square`. For now fall back to the base coords (the
    // server pads sequenceLength generously, so this is safe pre-wire).
    return [] as string[];
  }, []);
  const adapterCoords = useMemo(() => {
    if (myResponses.length === 0) {
      return coords;
    }
    const failedOrder: string[] = [];
    const failedSeen = new Set<string>();
    const solvedSet = new Set<string>();
    for (const raw of myResponses) {
      const firstColon = raw.indexOf(':');
      const lastColon = raw.lastIndexOf(':');
      if (firstColon === -1 || lastColon === firstColon) {
        continue;
      }
      const puzzleId = raw.slice(0, firstColon);
      const bit = raw.slice(lastColon + 1);
      if (bit === '1' || bit === 'true') {
        solvedSet.add(puzzleId);
      } else if (!failedSeen.has(puzzleId)) {
        failedSeen.add(puzzleId);
        failedOrder.push(puzzleId);
      }
    }
    const recycle = failedOrder.filter(id => !solvedSet.has(id));
    return recycle.length > 0 ? [...coords, ...recycle] : coords;
  }, [coords, myResponses]);

  // Current puzzle's hint (curated Demo-Call puzzles only). When present it
  // REPLACES the generic tip text under the board. Follows the same puzzle the
  // grid/board is showing (mine, or the followed player's when spectating).
  const currentHint = useMemo(() => {
    const idx = adapterCoords.length
      ? gridCurrentIndex % adapterCoords.length
      : 0;
    const pid = adapterCoords[idx];
    const p = pid ? (puzzles as Record<string, BattlePuzzle>)[pid] : undefined;
    const h = p?.hint;
    return h && h.trim() ? h.trim() : '';
  }, [adapterCoords, gridCurrentIndex, puzzles]);

  const adapter = useMemo(() => getAdapter(BattleType.PUZZLE_ROYALE), []);

  const totalSec = session?.settings.timerSec ?? 90;
  const endsAt = session?.endsAt ?? 0;
  const startsAt = session?.startsAt ?? 0;

  // ── 3-2-1-Go countdown ──────────────────────────────────────────────────
  // Both seeds compare against the SERVER's `startsAt`, so they read the
  // skew-corrected clock (util/serverClock.ts). On a device running fast the
  // raw-clock version decided the countdown was already over and skipped it.
  const [inCountdown, setInCountdown] = useState(
    () => !!startsAt && serverNowMs() < startsAt,
  );
  const [countdownMs, setCountdownMs] = useState(() =>
    startsAt ? msUntil(startsAt) : 0,
  );
  // Brief "Go!" flash the moment the countdown crosses zero.
  const [showGo, setShowGo] = useState(false);
  // Blocks MOVE submission until the local countdown + cushion has elapsed, so a
  // skewed client clock can't submit before the server's starts_at (see below).
  const [moveGrace, setMoveGrace] = useState(false);
  // SKEW-PROOF COUNTDOWN: the server stamps `starts_at` on ITS clock and rejects
  // any move before it (COUNTDOWN_IN_PROGRESS "Battle has not started yet"). If
  // the client counts down `startsAt - Date.now()` on a device clock that runs
  // FAST, the 3-2-1 finishes early, the board unblocks, the user clicks, and the
  // server rejects the move (real incident: solo, user 54644, ~2s-fast clock,
  // 2026-07-21). Fix (mirrors the timer pill's raceStartLocalRef): the moment we
  // first SEE a fresh startsAt, capture a LOCAL anchor and count down a fixed
  // DURATION (COUNTDOWN_MS) from it. Elapsed is a delta of the client's OWN
  // clock, so any constant offset cancels — the countdown length is correct no
  // matter how skewed the device clock is, and moves stay blocked for the full
  // real 3 seconds (+ a small cushion for server/network).
  const countdownAnchorRef = useRef<number | null>(null);
  const countdownStartsAtRef = useRef<number>(0);
  useEffect(() => {
    if (!startsAt) {
      countdownAnchorRef.current = null;
      countdownStartsAtRef.current = 0;
      setInCountdown(false);
      setCountdownMs(0);
      setMoveGrace(false);
      return;
    }
    // New session/countdown (startsAt changed) → re-anchor to local NOW. A
    // mid-race rejoin arrives with a startsAt already in the past, so the first
    // tick immediately computes remaining<=0 and we never gate (correct).
    if (countdownStartsAtRef.current !== startsAt) {
      countdownStartsAtRef.current = startsAt;
      // If startsAt is already in the past when we first see it (rejoin), don't
      // anchor a fresh 3s — the race is underway. Only anchor when the server
      // countdown is genuinely still ahead of us on our own clock.
      //
      // AND anchor only the time that's ACTUALLY LEFT, not a fresh full 3s.
      // Bug: a client whose fun_session_start landed late (envelope latency, or
      // just a slow render) could see startsAt only ~0.3s ahead and still run a
      // full 3-second overlay — so the 3-2-1 was still counting while the
      // server's race clock had already started, costing that player real race
      // time. Back-date the anchor by the elapsed portion so the overlay only
      // ever shows the remainder. Still a LOCAL duration (skew-proof): we clamp
      // the observed remaining to [0, COUNTDOWN_MS] and count that down.
      // Measured against the SERVER's clock: this decides whether a countdown
      // runs at all, and a fast device previously saw `startsAt` already past
      // and skipped straight to the race (Round-56's incident). The anchor
      // itself stays on the LOCAL clock below — the countdown remains a local
      // DURATION, which is what makes it immune to skew drift mid-count.
      const remainingAtFirstSight = msUntil(startsAt);
      if (remainingAtFirstSight > 0) {
        const consumed = Math.max(0, COUNTDOWN_MS - remainingAtFirstSight);
        countdownAnchorRef.current = Date.now() - consumed;
      } else {
        countdownAnchorRef.current = null;
      }
    }
    let goTimer: ReturnType<typeof setTimeout> | null = null;
    let graceTimer: ReturnType<typeof setTimeout> | null = null;
    const tick = () => {
      const anchor = countdownAnchorRef.current;
      // Duration-anchored when we saw a fresh countdown; else (rejoin) fall back
      // to the absolute startsAt (already past → remaining 0, no gating).
      const remaining =
        anchor != null
          ? Math.max(0, COUNTDOWN_MS - (Date.now() - anchor))
          : msUntil(startsAt);
      setCountdownMs(remaining);
      const wasIn = remaining > 0;
      setInCountdown(prev => {
        // On the transition from counting → started, flash "Go!" and hold move
        // submission for a short cushion so the first move lands after the
        // server's starts_at even accounting for processing/network.
        if (prev && !wasIn) {
          setShowGo(true);
          goTimer = setTimeout(() => setShowGo(false), 700);
          setMoveGrace(true);
          graceTimer = setTimeout(
            () => setMoveGrace(false),
            COUNTDOWN_MOVE_CUSHION_MS,
          );
        }
        return wasIn;
      });
      if (remaining <= 0) {
        clearInterval(id);
      }
    };
    tick();
    const id = setInterval(tick, 100);
    return () => {
      clearInterval(id);
      if (goTimer) clearTimeout(goTimer);
      if (graceTimer) clearTimeout(graceTimer);
    };
  }, [startsAt]);

  // ── Live seconds-left (SKEW-PROOF) ────────────────────────────────────────
  // BUG (classroom): the countdown was `endsAt - Date.now()`, comparing the
  // server's absolute end-time against the CLIENT's wall clock. A student whose
  // machine clock is skewed (e.g. 2 min fast) then SEES a wrong remaining time
  // (reported "3 min instead of 5"). The server always enforces the true end, so
  // only the DISPLAY was wrong.
  //
  // Fix (mirrors classroom ActivePuzzle, which counts down a server DURATION):
  // when the race starts we capture a LOCAL anchor `raceStartLocalRef = Date.now()`
  // and count down `totalSec - (Date.now() - anchor)`. Elapsed is a DELTA of the
  // client's own clock, so any constant skew cancels out — the displayed timer is
  // correct regardless of the machine's clock offset.
  //
  // Rejoin fallback: if we mount INTO an already-running race (no fresh countdown
  // seen), we have no local anchor, so we fall back to the absolute `endsAt`.
  // That fallback is NO LONGER skew-sensitive — it now measures against the
  // server's clock via `msUntil` (util/serverClock.ts), which the server feeds
  // with a `serverNow` stamp on every session/rejoin/spectate envelope.
  // Local-clock anchor = the moment WE observed the 3-2-1 countdown finish (a
  // purely client-side event — see the anchor effect below). Elapsed is then a
  // delta of the client's OWN clock, so any constant skew cancels out.
  const raceStartLocalRef = useRef<number | null>(null);
  const secondsFromAnchor = useCallback(() => {
    // Before the race starts (still in the 3-2-1), show the full duration.
    if (inCountdown) {
      return totalSec;
    }
    if (raceStartLocalRef.current != null) {
      const elapsed = (Date.now() - raceStartLocalRef.current) / 1000;
      return Math.max(0, Math.ceil(totalSec - elapsed));
    }
    // Rejoin fallback (mounted mid-race, never saw the countdown → no local
    // anchor): compute from the absolute end time against the SERVER's clock.
    // This used to be raw `Date.now()` and was the one royale path with no skew
    // protection at all — the "needs a server serverNow" note in the comment
    // above is now satisfied (see util/serverClock.ts).
    if (endsAt) {
      return Math.max(0, Math.ceil(msUntil(endsAt) / 1000));
    }
    return totalSec;
  }, [inCountdown, totalSec, endsAt]);

  // Anchor the local clock when the countdown FINISHES (inCountdown: true→false)
  // — a client-observed transition, no timestamp comparison, so it fires
  // regardless of clock skew. A mid-race rejoin never sees this (no countdown),
  // so the anchor stays null and we use the endsAt fallback.
  const prevInCountdownRef = useRef(inCountdown);
  useEffect(() => {
    if (
      prevInCountdownRef.current &&
      !inCountdown &&
      raceStartLocalRef.current == null
    ) {
      raceStartLocalRef.current = Date.now();
    }
    prevInCountdownRef.current = inCountdown;
  }, [inCountdown]);

  const [secondsLeft, setSecondsLeft] = useState(() => secondsFromAnchor());
  useEffect(() => {
    if (!endsAt || status === 'ended') {
      return;
    }
    setSecondsLeft(secondsFromAnchor());
    const id = setInterval(() => {
      const next = secondsFromAnchor();
      setSecondsLeft(next);
      if (next <= 0 && !inCountdown) {
        clearInterval(id);
      }
    }, 200);
    return () => clearInterval(id);
  }, [endsAt, status, inCountdown, secondsFromAnchor]);
  // Reset the anchor when a NEW session begins (rematch reuses this component).
  useEffect(() => {
    raceStartLocalRef.current = null;
    prevInCountdownRef.current = inCountdown;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Stable clock seed for the reused PlayChess ClockComponent (it owns its own
  // countdown and restarts whenever this value changes — so it must NOT be the
  // per-second `secondsLeft` tick). Same skew-proof source as secondsLeft.
  const clockSeconds = useMemo(() => {
    return secondsFromAnchor();
    // Intentionally keyed only on the stable session values, NOT on a ticking
    // value, so the clock isn't restarted every render. inCountdown flips once
    // at start (full duration → running), which is the intended re-seed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endsAt, startsAt, totalSec, inCountdown]);

  // Board sizing — fill the available column (fixes the "board not occupying
  // full free space" note). Cap so it stays sensible on huge monitors.
  const boardWidth = useMemo(() => {
    // The board must ALSO fit the viewport HEIGHT so the bottom rank is never
    // cut off (no scrolling to see the last row), but make it as LARGE as fits.
    // Above the board: app header (~135px) + page padding (~28px) + the status
    // strip (~72px) ≈ 235px; reserve that and use the rest for the board.
    // The end result now OVERLAYS the board (no banner above it), so ended uses
    // the same generous vertical budget as an in-play race — board stays large
    // (#1, #4). Only a slim who-to-play row sits above the board in-battle.
    const heightCap = Math.max(320, viewportHeight - 235);
    if (isWide) {
      // During a race AND after it ends the board's center column is flanked by
      // the grid + race track; only the WAITING lobby has just board + wide
      // rail. Size the board to fill its column without overflowing (#1, #7).
      const flanked = status === 'in_battle' || status === 'ended';
      const widthCap = (viewportWidth - 72) * (flanked ? 0.58 : 0.6);
      return Math.min(900, heightCap, widthCap);
    }
    return Math.min(620, heightCap, viewportWidth - 48);
  }, [isWide, viewportWidth, viewportHeight, status]);

  // Side-to-move for the player's CURRENT puzzle, for the strip's who-to-play
  // pill. The adapter pushes the puzzle's setup ply (plies[0]) before the
  // player moves, so the side that actually moves is the OPPOSITE of the FEN's
  // native active-color. Derive it cheaply from the FEN string (field 2) — no
  // chess.js needed; falls back to white if the puzzle isn't loaded yet.
  const toMove = useMemo<'white' | 'black'>(() => {
    const id = adapterCoords[myIndex];
    const fen = id ? (puzzles as any)[id]?.fen : undefined;
    if (typeof fen !== 'string') {
      return 'white';
    }
    const active = fen.split(' ')[1];
    // Native turn 'w' → after setup it's Black to move, and vice-versa.
    return active === 'w' ? 'black' : 'white';
  }, [adapterCoords, myIndex, puzzles]);

  const battleDisabled =
    status !== 'in_battle' ||
    !session ||
    secondsLeft <= 0 ||
    inCountdown ||
    // Hold moves for a short cushion after the (skew-proof, duration-anchored)
    // countdown ends, so the first move lands after the server's starts_at and
    // isn't rejected with COUNTDOWN_IN_PROGRESS (see the countdown effect above).
    moveGrace ||
    !!me?.eliminated ||
    // A watching coach is a wire-player but must never submit moves — their
    // board is the read-only spectate view (belt-and-suspenders; the playable
    // panel isn't mounted for them anyway).
    effectiveSpectator;

  const onMove = useCallback(
    (correct: boolean, square: string) => {
      if (!session) {
        return;
      }
      sendMove(session.sessionId, correct ? 1 : 0, square);
    },
    [sendMove, session],
  );

  // Spectate per-ply stream (#3): the playing client emits every walker advance
  // (own correct ply + auto-reply + new-puzzle setup); the server fans it out to
  // spectators so a followed board animates move-by-move. No-op for spectators.
  const onPly = useCallback(
    (
      fen: string,
      from: string,
      to: string,
      orientation: 'white' | 'black',
      kind: 'setup' | 'player' | 'reply' | 'wrong',
    ) => {
      if (!session || isSpectator) {
        return;
      }
      spectateMove(session.sessionId, fen, from, to, orientation, kind);
    },
    [spectateMove, session, isSpectator],
  );

  // Leave the race mid-game (forfeit). Confirmed via a dialog (item 6). On
  // confirm: tell the server (it marks us out — non-terminating, the race
  // continues for everyone else — and frees our active-session lock), then drop
  // local royale state and return to the Fun Challenges lobby. Navigating away
  // also stops us moving pieces (item 3).
  const [confirmLeave, setConfirmLeave] = useState(false);
  // The end result is OVERLAID on the board (#4), not auto-popped. The full
  // standings MODAL is opt-in — opened via the overlay's "View results" and
  // closed back to the overlay. Default hidden; reset when a new session begins.
  const [showStandingsModal, setShowStandingsModal] = useState(false);
  useEffect(() => {
    if (status !== 'ended') {
      setShowStandingsModal(false);
    }
  }, [status]);

  // POST-GAME REVIEW. When the race ends we show the result overlay on the board;
  // the player can dismiss it (X) or click any COMPLETED puzzle in the grid, which
  // (a) hides the overlay, moving the result card BELOW the board, and (b) loads
  // that puzzle's start position (locked, "to play") onto the main board. Only
  // active once ended — never during play. Reset on a new session.
  const [endDismissed, setEndDismissed] = useState(false);
  const [reviewIndex, setReviewIndex] = useState<number | null>(null);
  useEffect(() => {
    if (status !== 'ended') {
      setEndDismissed(false);
      setReviewIndex(null);
    }
  }, [status]);

  // Item 5: once the host taps Start we disable the button until the server
  // responds (the session starts → status flips). A second click before the
  // ACK could double-fire fun_room_start. We also auto-clear after a timeout so
  // a failed start re-enables it.
  const [starting, setStarting] = useState(false);
  useEffect(() => {
    // Any non-lobby status (started, or reset back to idle) clears the guard.
    if (status !== 'lobby') {
      setStarting(false);
    }
  }, [status]);
  useEffect(() => {
    if (!starting) {
      return;
    }
    const id = setTimeout(() => setStarting(false), 6000);
    return () => clearTimeout(id);
  }, [starting]);
  // If the server rejects the start (e.g. the puzzle-sequence fetch timed out:
  // "Couldn't fetch puzzle sequence … ESOCKETTIMEDOUT"), clear the starting
  // guard IMMEDIATELY so the host can Retry without waiting out the 6s timeout.
  // The lobby is intentionally NOT reset on these errors (see fun_error
  // routing), so Retry just re-fires startRace on the same room.
  useEffect(() => {
    if (errorMessage) {
      setStarting(false);
    }
  }, [errorMessage]);

  // Recover a client that's stuck in the WAITING ROOM. fun_session_start is a
  // one-shot broadcast — if this client's socket was mid-(re)connect or its
  // handler wasn't attached at the instant the host started, it misses the
  // message and sits in the lobby forever ("sometimes it doesn't start"). While
  // we're in 'lobby', poll the server: fun_session_rejoin replies with the live
  // session (keyed on our userId) the moment one exists, which transitions us
  // into the race via routeRoyaleSessionRejoin. A NO_ACTIVE_SESSION reply is
  // silently ignored (probe requestId), so this is harmless before the host
  // starts. Stops the instant status leaves 'lobby'.
  useEffect(() => {
    if (status !== 'lobby') {
      return;
    }
    const id = setInterval(() => {
      probeSession();
    }, 2500);
    return () => clearInterval(id);
  }, [status, probeSession]);

  // HARD-REFRESH REJOIN. On a fresh load at /PuzzleRoyaleBattle the royale store
  // is empty (status 'idle'), so the lobby-poll above never runs AND the global
  // auto-probe in useFunChallengesSocket sends the wrong gameType
  // ('fun_square_trainer'), so the server never returns our royale session —
  // progress is lost until you re-enter via Fun Challenges. Fix: fire a
  // ROYALE-typed probeSession() on mount (and retry a few times while still
  // idle, since the socket may not be authed yet). The reply routes through
  // routeRoyaleSessionRejoin → hydrateRoyale. Idempotent + requestId-suppressed,
  // so it's a no-op when there's genuinely no active royale.
  useEffect(() => {
    if (status !== 'idle') {
      return;
    }
    probeSession();
    let tries = 0;
    const id = setInterval(() => {
      tries += 1;
      const stillIdle =
        useFunChallengesRoyaleStore.getState().status === 'idle';
      if (!stillIdle) {
        clearInterval(id);
        return;
      }
      if (tries > 6) {
        // No active royale to rejoin (e.g. a stale /PuzzleRoyaleBattle URL or a
        // finished race) — don't sit on "Setting up the race…" forever; send
        // the user to the Fun Challenges lobby (#1).
        clearInterval(id);
        try {
          navigation.navigate('FunChallengesLobby');
        } catch {
          // non-fatal
        }
        return;
      }
      probeSession();
    }, 1500);
    return () => clearInterval(id);
  }, [status, probeSession, navigation]);

  // Spectator: stop watching → tell the server to drop us from the spectator
  // set (so a clean rejoin works — #8), then drop royale state + back to lobby.
  const stopWatching = useCallback(() => {
    const roomId = session?.roomId || lobby?.roomId;
    if (roomId) {
      leaveLobby(roomId);
    }
    useFunChallengesRoyaleStore.getState().reset();
    try {
      navigation.navigate('FunChallengesLobby');
    } catch {
      // non-fatal
    }
  }, [navigation, leaveLobby, session, lobby]);

  const handleLeaveRace = useCallback(() => {
    setConfirmLeave(false);
    if (session) {
      forfeit(session.sessionId);
    }
    useFunChallengesRoyaleStore.getState().reset();
    try {
      navigation.navigate('FunChallengesLobby');
    } catch {
      // non-fatal
    }
  }, [forfeit, session, navigation]);

  // Classroom royale: register the INITIAL lobby with Django so students get
  // the Join banner. We deliberately do NOT register REMATCH lobbies: a rematch
  // spins up a brand-new room server-side, and registering each one created a
  // fresh "active" classroom row whose ended_at only gets stamped if THAT exact
  // room finishes/leaves cleanly. When the coach stopped on a rematch room that
  // was created-but-not-finished, its row stayed active until the 15-min TTL —
  // pinning the classroom button to "Open Puzzle Royale" (even on refresh) long
  // after the royale was actually over. The classroom row tracks only the
  // coach's original create; rematches are a player-side continuation and don't
  // need their own classroom-launcher row (we don't re-notify students either).
  // Only the HOST registers, once, when this royale originated in a classroom.
  // NOTE: this hook MUST stay above the idle-guard early return below, or React
  // throws "rendered fewer hooks than expected" when the guard fires. It
  // recomputes isHost inline (the render-scope `isHost` is declared after the
  // guard) so it has no dependency on post-guard locals.
  useEffect(() => {
    const hId = lobby?.hostId || (session?.settings?.hostId as string) || '';
    const iAmHost = !!hId && String(hId) === String(myUserId);
    // CRITICAL: only register the REAL roomId ("fun_room_..."), never a share
    // code. On the coach's reopen tab (joined by code) the optimistic seed sets
    // lobby.roomId = the share CODE until fun_room_update backfills the real id.
    // Registering the code as room_id meant the server's end-of-game ended_at
    // stamp (keyed on the real roomId) never matched → the row stayed un-ended →
    // the coach couldn't create the next royale (#2). Wait for the real id.
    const realRoomId = lobby?.roomId || '';
    const isRealRoomId = realRoomId.indexOf('fun_room_') === 0;
    if (
      !iAmHost ||
      !classroomContext ||
      !isRealRoomId ||
      !lobby?.shareCode ||
      registeredRoyaleRoomsRef.current.has(realRoomId)
    ) {
      return;
    }
    // If we've ALREADY registered a room from this tab, any new room is a
    // REMATCH — mark it seen (so we don't retry) but skip the Django row.
    const isRematchRoom = registeredRoyaleRoomsRef.current.size > 0;
    registeredRoyaleRoomsRef.current.add(realRoomId);
    if (isRematchRoom) {
      return;
    }
    registerClassroomRoyale({
      meetingId: classroomContext.meetingId,
      occurenceId: classroomContext.occurenceId,
      roomId: realRoomId,
      shareCode: lobby.shareCode,
      settings: lobby.settings,
      expiresAt: lobby.expiresAt,
    });
  }, [lobby, session, myUserId, classroomContext, registerClassroomRoyale]);

  // ── Idle guard ──────────────────────────────────────────────────────────
  // Shared reconnect/auth overlay — identical to FunChallengeBattle (PDB/PTB).
  // Rendered in EVERY top-level return below so a displaced or unauthenticated
  // tab can always reclaim the socket instead of feeling stuck. ConnectionNotice
  // is the socket-level disconnect banner; ConnectionModal is the multi-tab /
  // auth-gate dialog. Its overlay is position:absolute, so the later-painted
  // board would cover it — wrap in a viewport-fixed, high-zIndex layer.
  const connectionOverlay = (
    <>
      <ConnectionNotice />
      {(!!changeClient || !authComplete) && (
        <View
          style={
            {
              position: 'fixed' as any,
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
            } as any
          }>
          <ConnectionModal
            visible
            onContinue={() => {
              setChangeClient?.(false);
              reconnectSocket();
            }}
            onClose={() => navigation.navigate('Dashboard')}
            authFail={!authComplete}
          />
        </View>
      )}
    </>
  );

  // Show something only once there's a lobby (waiting room) OR a session
  // (race). Before either exists we're mid-navigation.
  //
  // SOLO has NO waiting room — startSolo (and a solo rematch) create the room
  // then auto-start it, so the `lobby` phase is a brief transient. Rendering the
  // multiplayer waiting-room chrome for that beat caused a visible "lobby flash"
  // before fun_session_start flips to in_battle. For solo we treat `lobby` as
  // "starting" and show the countdown loader instead — no waiting room ever.
  const isLive = status === 'in_battle' || status === 'ended';
  const isWaiting = !isSolo && status === 'lobby' && !!lobby;
  const isSoloStarting = isSolo && status === 'lobby';
  if (isSoloStarting || (!isWaiting && !isLive)) {
    return (
      <View style={styles.emptyRoot}>
        {connectionOverlay}
        {/* The chessboard CCLoader in COMPONENT mode — just the Lottie (no
            dark-scrim full-screen overlay), centered in our cream emptyRoot.
            We keep our own caption below it. Component mode sizes the Lottie to
            WINDOW/4, which fits this full-screen container (the earlier
            ActivityIndicator workaround was for the COMPACT lobby panel slot,
            where the window-fraction Lottie clipped — not an issue here). */}
        <CCLoader loading isComponentLoader />
        <CCText style={styles.emptyText}>
          {isSoloStarting ? 'Starting your game…' : 'Setting up the race…'}
        </CCText>
      </View>
    );
  }

  const isEnded = status === 'ended';
  // Latch true once ANY solo game has ended this session (survives a rematch) —
  // gates the solo leaderboard's refetch-on-focus. The ref is declared ABOVE the
  // early returns (with the other hooks) — see soloEndedOnceRef; only the
  // assignment is done here.
  if (isSolo && isEnded) {
    soloEndedOnceRef.current = true;
  }
  const soloHasEndedThisSession = soloEndedOnceRef.current;
  // The 3-column layout (left grid panel · board · race track) applies during an
  // in-play race AND after it ends — so the player keeps their per-puzzle grid +
  // the standings while reviewing the result (#3, #6). Only the WAITING lobby
  // uses the wide board + wide rail (old lobby proportions).
  const showGridPanel = status === 'in_battle' || isEnded;
  // On mobile/tablet the puzzle-grid panel stacks ABOVE the board and eats
  // scarce vertical space — hide it so the board + race track render larger.
  // Uses the app-wide `isMobileOrTab` flag (screenWidth <= 768, reactive to
  // dimension changes) so this matches how the rest of the app defines
  // mobile/tablet. Only the GRID render is gated on this — all other layout
  // still keys off showGridPanel unchanged.
  const showGridPanelResponsive = showGridPanel && !isMobileOrTab;
  const myRank = leaderboard.findIndex(id => id === myUserId);
  // Host id lives on the lobby (waiting room) or, once the race has started and
  // the lobby is cleared, on the session settings — so the end screen still
  // knows who can rematch.
  const hostId = lobby?.hostId || (session?.settings?.hostId as string) || '';
  const isHost = !!hostId && String(hostId) === String(myUserId);
  // A SCHEDULED TOURNAMENT room must never offer a rematch (#2): the tournament
  // is a fixed-time event whose standings are persisted against the Django row,
  // so a rematch would spin up an ad-hoc room that isn't part of the tournament
  // at all. `onRoyaleTournamentStart` stamps `isTournament` on the room settings,
  // which reach us via the lobby pre-start and the session settings after start.
  const isTournamentRoom = !!(
    (lobby?.settings as any)?.isTournament ||
    (session?.settings as any)?.isTournament
  );
  // A spectate-host (hostIsPlayer:false) is not a player — exclude them from the
  // lobby player count shown in the caption (#1).
  const lobbyPlayerCount = isWaiting
    ? (lobby!.players || []).filter(
        p =>
          !(
            lobby!.hostIsPlayer === false &&
            !!lobby!.hostId &&
            String(p.userId) === String(lobby!.hostId)
          ),
      ).length
    : leaderboard.length;
  const playerCount = lobbyPlayerCount;

  // Human-readable topic summary for the pre-start info line (#5). Empty topics
  // means "any theme".
  const topicSummary = (() => {
    const topics = (lobby?.settings.topics || []) as string[];
    if (!topics.length) {
      return 'All topics';
    }
    const labels = topics.map(t => TOPIC_LABELS[t] || t);
    if (labels.length <= 2) {
      return labels.join(', ');
    }
    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
  })();

  const gamePanel =
    isLive && adapter ? (
      adapter.renderGame({
        coords: adapterCoords,
        myIndex,
        boardWidth,
        puzzles,
        onMove,
        onPly,
        disabled: battleDisabled,
        hideCounter: true,
        hidePrompt: true,
      })
    ) : (
      // Pre-start: a real board at the standard starting position (read-only),
      // so the screen looks like a chess game getting ready, not a blank panel.
      // No caption below it (#2) — the waiting card above + the right-rail Start
      // button already carry the status, and dropping it lets the board grow.
      <View style={styles.waitingBoardWrap}>
        <View
          style={{width: boardWidth, alignSelf: 'center'}}
          pointerEvents="none">
          <ChessboardWithArrow
            position={STARTING_FEN}
            boardOrientation="white"
            colorTheme={chessboardColorTheme}
            disabled
            animationDuration={0}
          />
        </View>
      </View>
    );

  // End result OVERLAID on the board (#4) instead of a popup modal: a
  // translucent backdrop over the board carries the RoyaleEndScreen card
  // (stats + Rematch/View results/Exit). Stays until the player rematches or
  // exits; "View results" still opens the full standings modal.
  const endOverlay = (
    <View style={styles.endOverlay}>
      <View style={{width: Math.min(boardWidth - 24, 460)}}>
        <RoyaleEndScreen
          solo={isSolo}
          me={effectiveSpectator ? undefined : me}
          myRank={effectiveSpectator ? undefined : myRank}
          totalPlayers={displayLeaderboard.length}
          // Spectators + a watching coach (no `me`) see the standings inline —
          // winner + points + best combo per player — instead of an empty card
          // (#3). Coach's own row is filtered out of the display data.
          players={effectiveSpectator ? displayPlayers : undefined}
          leaderboard={effectiveSpectator ? displayLeaderboard : undefined}
          canRematch={isHost && !classroomContext && !isTournamentRoom}
          onRematch={() => {
            if (session) rematch(session.sessionId);
          }}
          // "View results" REMOVED (user, 2026-08-01): redundant. The full
          // standings are already on screen in the right rail's Players tab and
          // in the below-board card, so a modal repeating them added a click for
          // nothing. Passing `undefined` hides the button — the same mechanism
          // solo already used — so `RoyaleEndScreen` itself is untouched and
          // ad-hoc royale keeps working.
          onViewResults={undefined}
          // Close (X): dismiss the on-board overlay → the same result renders
          // below the board and completed-puzzle review unlocks. Players only —
          // spectators have no below-board card, so they keep the persistent
          // overlay (no X).
          onClose={
            effectiveSpectator ? undefined : () => setEndDismissed(true)
          }
          onExit={() => {
            // A server spectator stops watching (server drop); a watching coach
            // is a wire-player, so exit like a player (reset + navigate away).
            if (isSpectator) {
              stopWatching();
              return;
            }
            useFunChallengesRoyaleStore.getState().reset();
            try {
              navigation.navigate('FunChallengesLobby');
            } catch {
              // non-fatal
            }
          }}
        />
      </View>
    </View>
  );

  // ── Post-game review board ────────────────────────────────────────────────
  // When ended and the player has clicked a completed puzzle, show THAT puzzle's
  // start position (the FEN they actually saw) on the main board, LOCKED (not
  // movable), with a "White/Black to play" + correct/wrong caption. Reused for
  // solo, multiplayer, and classroom (all drive the same grid + gridFenForIndex).
  const reviewing = isEnded && reviewIndex != null;
  const reviewFen = reviewing ? gridFenForIndex(reviewIndex!) : undefined;
  const reviewToPlay: 'white' | 'black' =
    reviewFen && reviewFen.split(' ')[1] === 'b' ? 'black' : 'white';
  const reviewBoard =
    reviewing && reviewFen ? (
      // Absolute overlay covering the (still-mounted) game board — no component
      // swap. A solid backdrop hides the board beneath so only the review shows.
      <View style={styles.reviewOverlay} pointerEvents="box-none">
        <View
          style={{width: boardWidth, alignSelf: 'center'}}
          pointerEvents="none">
          <ChessboardWithArrow
            position={reviewFen}
            boardOrientation={reviewToPlay}
            colorTheme={chessboardColorTheme}
            disabled
            animationDuration={0}
          />
        </View>
        {/* No caption below the review board — the top indicator carries the
            to-play, and the grid box colour + selection ring carry which puzzle
            and its outcome. */}
      </View>
    ) : null;

  // The compact result card shown BELOW the board (replacing the hint) once the
  // on-board overlay is dismissed (X or a completed-puzzle click). Same stats +
  // actions as the overlay card, minus the close X. Players only.
  const belowBoardResult =
    isEnded && endDismissed && !effectiveSpectator && me ? (
      <View style={[styles.belowResultWrap, {maxWidth: boardWidth}]}>
        <RoyaleEndScreen
          compact
          solo={isSolo}
          me={me}
          myRank={myRank}
          totalPlayers={displayLeaderboard.length}
          canRematch={isHost && !classroomContext && !isTournamentRoom}
          onRematch={() => {
            if (session) rematch(session.sessionId);
          }}
          // Removed alongside the overlay's — see the note there.
          onViewResults={undefined}
          onExit={() => {
            useFunChallengesRoyaleStore.getState().reset();
            try {
              navigation.navigate('FunChallengesLobby');
            } catch {
              // non-fatal
            }
          }}
        />
      </View>
    ) : null;

  // IMPORTANT: the root is a plain View, NOT a ScrollView. react-chessboard
  // computes drop squares from the board's on-screen rect; a parent ScrollView
  // offsets pointer coordinates mid-drag so pieces land on the WRONG square
  // (item 2). BattleScreen (PTB/PDB) uses a View root for exactly this reason.
  // We give the RIGHT rail its own internal ScrollView so long rosters still
  // scroll, while the board column never sits inside a scroll container.
  return (
    <View style={styles.root}>
      {connectionOverlay}
      {/* Error banner with Retry. The puzzle-sequence fetch can time out on the
          server (ESOCKETTIMEDOUT) when the host starts — that comes back as a
          royale fun_error WITHOUT closing the lobby, but the host previously saw
          nothing and felt stuck. Show the message + a Retry that re-fires Start
          (host) on the SAME lobby, plus a dismiss. */}
      {!!errorMessage && (
        <View style={styles.errorBanner}>
          <CCText style={styles.errorBannerText} numberOfLines={3}>
            {errorMessage}
          </CCText>
          <View style={styles.errorBannerActions}>
            {isHost && isWaiting && !!lobby && (
              <Pressable
                onPress={() => {
                  clearError();
                  if (!starting) {
                    setStarting(true);
                    startRace(lobby.roomId, {
                      hostIsPlayer: lobby.hostIsPlayer,
                    });
                  }
                }}
                style={styles.errorRetryBtn}>
                <CCText style={styles.errorRetryText}>Retry</CCText>
              </Pressable>
            )}
            <Pressable onPress={clearError} style={styles.errorDismissBtn}>
              <CCText style={styles.errorDismissText}>Dismiss</CCText>
            </Pressable>
          </View>
        </View>
      )}
      <View
        style={[
          styles.scrollContent,
          styles.layout,
          isWide ? styles.layoutWide : styles.layoutNarrow,
        ]}>
        {/* LEFT column (UI-2, ~15%): the puzzle-grid panel during a live race —
            combo badge + correct/wrong + 10×7 grid + Forfeit. Shows MY progress,
            or the FOLLOWED player's when spectating. Hidden while waiting; the
            ended state renders the result MODAL over the whole screen instead.
            Hidden entirely on mobile/tablet (narrow) so the board grows. */}
        {showGridPanelResponsive ? (
          <View style={[styles.gridCol, isWide && styles.gridColWide]}>
            {isSpectator && (
              <View style={styles.watchingRow}>
                <View style={styles.watchingDot} />
                <CCText style={styles.watchingText} numberOfLines={1}>
                  {followed?.name || 'Watching'}
                </CCText>
                <Pressable onPress={stopWatching} style={styles.stopChip}>
                  <CCText style={styles.stopChipText}>Stop</CCText>
                </Pressable>
              </View>
            )}
            <RoyalePuzzleGridPanel
              outcomes={gridOutcomes}
              currentIndex={gridCurrentIndex}
              combo={gridOwner?.combo ?? 0}
              solved={gridOwner?.solved ?? 0}
              wrong={gridOwner?.wrong ?? 0}
              total={gridTotal}
              fenForIndex={gridFenForIndex}
              ownerLabel={isSpectator ? followed?.name : 'You'}
              showForfeit={
                SHOW_LEAVE_RACE &&
                !isSpectator &&
                status === 'in_battle' &&
                !me?.eliminated
              }
              onForfeit={() => setConfirmLeave(true)}
              // Post-game only (and not for spectators): click a completed box
              // to review it on the main board. Clicking also dismisses the
              // on-board result overlay (result moves below the board).
              onSelect={
                isEnded && !effectiveSpectator
                  ? (index: number) => {
                      setReviewIndex(index);
                      setEndDismissed(true);
                    }
                  : undefined
              }
              selectedIndex={isEnded ? reviewIndex : null}
            />
          </View>
        ) : null}

        {/* CENTER column. During a race/ended it takes ~50% (grid + track flank
            it); the waiting lobby widens to the old board proportions (#1, #5).
            Header above the board: who-to-play + clock (in-play), a compact end
            summary (ended, #3/#6), or the lobby card (waiting). */}
        <View
          style={[
            styles.centerCol,
            isWide && (showGridPanel ? styles.centerColRace : styles.centerColWide),
          ]}>
          {/* Spectator "Watching <name> · Stop" row. Normally lives inside the
              grid panel, but that panel is hidden on mobile/tablet — so when
              spectating in-race on a narrow layout, surface it here (above the
              board) so the spectator keeps a Stop affordance. */}
          {showGridPanel && !showGridPanelResponsive && isSpectator ? (
            <View style={[styles.watchingRow, {width: boardWidth}]}>
              <View style={styles.watchingDot} />
              <CCText style={styles.watchingText} numberOfLines={1}>
                {followed?.name || 'Watching'}
              </CCText>
              <Pressable onPress={stopWatching} style={styles.stopChip}>
                <CCText style={styles.stopChipText}>Stop</CCText>
              </Pressable>
            </View>
          ) : null}
          {/* Who-to-play + clock. Kept in the ENDED state too (#1) — the clock
              simply reads 0:00 (playing=false). Only the waiting lobby swaps it
              for the lobby card. */}
          {showGridPanel ? (
            <View style={[styles.whoRow, {width: boardWidth}]}>
              <View style={styles.whoLeft}>
                {/* When reviewing a completed puzzle post-game, this top
                    indicator reflects the REVIEW puzzle's side-to-move (item 3);
                    otherwise the live game's toMove. */}
                <View
                  style={[
                    styles.whoColorBox,
                    (reviewing ? reviewToPlay : toMove) === 'black' &&
                      styles.whoColorBoxBlack,
                  ]}
                />
                <CCText style={styles.whoText}>
                  {(reviewing ? reviewToPlay : toMove) === 'black'
                    ? 'Black'
                    : 'White'}{' '}
                  to play
                </CCText>
              </View>
              <RoyaleClockPill
                endsAt={endsAt}
                startsAt={startsAt}
                seconds={clockSeconds}
                playing={status === 'in_battle' && !inCountdown}
              />
            </View>
          ) : isWaiting ? (
            <View style={styles.waitCard}>
              <CCText style={styles.waitCardTitle}>Puzzle Royale</CCText>
              {/* Iconified settings chips (modern look) instead of a "·"-joined
                  text line. */}
              <View style={styles.waitChips}>
                <WaitChip icon="timer-outline" text={`${lobby!.settings.timerSec}s`} />
                <WaitChip
                  icon="speedometer"
                  text={lobby!.settings.difficulty}
                  capitalize
                />
                <WaitChip icon="tag-multiple" text={topicSummary} />
                <WaitChip
                  icon="account-group"
                  text={`${playerCount} player${playerCount === 1 ? '' : 's'}`}
                />
              </View>
            </View>
          ) : null}

          {/* Spectator board: the followed player's board (read-only). Stays
              mounted through the ENDED state too (showing their final board)
              so a spectator isn't left staring at a blank panel (#6) — the end
              banner above + the standings on the rail complete the picture. */}
          {isLive && effectiveSpectator ? (
            <View style={styles.boardWrap}>
              <RoyaleSpectateView
                coords={adapterCoords}
                puzzles={puzzles}
                followed={followed}
                liveBoard={spectating ? liveBoards[spectating] ?? null : null}
                boardWidth={boardWidth}
              />
              {/* Spectators used to be left on a bare "Final standings…" pill
                  (#3). Show them the SAME result overlay players get — final
                  standings + winner + best combos — the instant the race ends OR
                  the clock hits 0 (covers a dropped fun_session_end). */}
              {(isEnded || (status === 'in_battle' && secondsLeft <= 0)) &&
                endOverlay}
            </View>
          ) : (
            <View style={styles.boardWrap}>
              {/* The game board stays mounted the whole time (never swap the
                  board component type — that unmount/remount was fragile). When
                  reviewing a completed puzzle post-game, the locked review board
                  is layered ON TOP as an absolute overlay. */}
              {gamePanel}
              {reviewBoard}
              {isLive && (inCountdown || showGo) && (
                <View style={styles.countdownOverlay} pointerEvents="none">
                  <View style={styles.countdownPill}>
                    {inCountdown ? (
                      <>
                        <CCText style={styles.countdownLabel}>Get ready</CCText>
                        <CCText style={styles.countdownDigit}>
                          {Math.min(
                            3,
                            Math.max(1, Math.ceil(countdownMs / 1000)),
                          )}
                        </CCText>
                      </>
                    ) : (
                      <CCText style={styles.countdownGo}>Go!</CCText>
                    )}
                  </View>
                </View>
              )}
              {status === 'in_battle' && !inCountdown && secondsLeft <= 0 && (
                <View style={styles.countdownOverlay} pointerEvents="none">
                  <View style={styles.countdownPill}>
                    <CCText style={styles.countdownLabel}>Time's up</CCText>
                    <CCText style={styles.finalizingText}>
                      Final standings…
                    </CCText>
                  </View>
                </View>
              )}
              {/* On-board overlay stays until dismissed (X or completed-puzzle
                  click). After dismissal the result renders below the board. */}
              {isEnded && !endDismissed && endOverlay}
            </View>
          )}

          {/* Below the board: once the game has ended AND the result overlay is
              dismissed, the RESULT card (stats + actions) takes this slot
              (replacing the hint). Otherwise the tip card — shown in every other
              phase (lobby, race, ended-but-overlay-still-up). A curated Demo-Call
              puzzle's own hint (currentHint) REPLACES the generic tip. */}
          {belowBoardResult ?? (
            <View style={[styles.boardTipCard, {maxWidth: boardWidth}]}>
              <CCText style={styles.boardTip}>
                {currentHint
                  ? `💡 ${currentHint}`
                  : '💡 Keep solving puzzles to grow your streak and earn more points!'}
              </CCText>
            </View>
          )}

          {/* Forfeit lives in the left grid panel; the ended state is handled by
              the on-board result overlay + the opt-in standings modal. */}
        </View>

        {/* Right column: 3-tab rail (Race / Players / Spectators).
            Its OWN ScrollView so a long roster/leaderboard scrolls here —
            never the whole page — keeping the board out of any scroll parent. */}
        <ScrollView
          style={[
            styles.rightCol,
            isWide && (showGridPanel ? styles.rightColRace : styles.rightColWide),
          ]}
          contentContainerStyle={styles.rightColContent}
          showsVerticalScrollIndicator={false}>
          <RoyaleRightRail
            isSolo={isSolo}
            soloTimeControl={soloTimeControl}
            soloHasEndedThisSession={soloHasEndedThisSession}
            // Ghost + "new best" compare against the PRE-GAME baseline (frozen
            // per session) — never the live-refetched PB.
            soloBestPoints={soloBaseline?.points ?? 0}
            soloHasPb={!!soloBaseline?.hasPb}
            soloCurrent={{
              // Streak = the PEAK combo reached this game (bestCombo), NOT the
              // live combo (me.combo) — the live one resets to 0 on a wrong
              // solve, which wrongly dropped the header to 0 after you'd already
              // beaten your best-streak record. bestCombo only ever climbs.
              streak: me?.bestCombo ?? 0,
              points: me?.points ?? 0,
              solves: me?.solved ?? 0,
            }}
            // "Best N" caption shows your record as it grows: max(baseline, live).
            soloBest={{
              streak: soloBaseline?.combo ?? 0,
              points: soloBaseline?.points ?? 0,
              solves: soloBaseline?.solves ?? 0,
            }}
            isWaiting={isWaiting}
            isHost={isHost}
            isSpectator={effectiveSpectator}
            isServerSpectator={isSpectator}
            spectating={spectating}
            onFollow={setSpectating}
            spectators={spectators}
            myUsername={myUsername}
            shareCode={
              lobby?.shareCode ||
              (session?.settings?.shareCode as string) ||
              ''
            }
            lobby={lobby}
            sessionSettings={session?.settings}
            players={displayPlayers}
            leaderboard={displayLeaderboard}
            joinOrder={joinOrder}
            myUserId={myUserId}
            // In a classroom the host's Play/Watch choice is CLIENT-SIDE only —
            // the coach stays a wire-player (hostIsPlayer:true) either way so a
            // lone student can start + the result saves; "Watch" just flips the
            // local coachWatchPref (read-only board following the student).
            // Standalone keeps the original server host-role behaviour.
            hostIsPlayer={
              classroomContext ? !coachWatchPref : lobby?.hostIsPlayer ?? true
            }
            onSetHostIsPlayer={(v: boolean) => {
              if (classroomContext) {
                // v = "host is a player". Watch = !v.
                setCoachWatchPref(!v);
                return;
              }
              setHostIsPlayer(v); // optimistic local
              // Tell the server so OTHER players' rosters update too (#5).
              if (lobby?.roomId) {
                setHostRole(lobby.roomId, v);
              }
            }}
            starting={starting}
            onStart={() => {
              if (lobby && !starting) {
                setStarting(true);
                // Classroom: ALWAYS start as a wire-player (hostIsPlayer:true),
                // even when the coach chose Watch — so the ≥2 gate + results
                // hold. `hostWatching` tells the server to register the coach as
                // a spectator (live board) and mark them a watcher so every
                // client hides their row. Standalone honours the lobby's host
                // role and never watches.
                startRace(lobby.roomId, {
                  hostIsPlayer: classroomContext ? true : lobby.hostIsPlayer,
                  hostWatching: classroomContext ? coachWatchPref : false,
                });
              }
            }}
            onLeave={() => {
              if (lobby) leaveLobby(lobby.roomId);
              useFunChallengesRoyaleStore.getState().reset();
              // Return to the Fun Challenges lobby (not stuck on the now-empty
              // battle screen).
              try {
                navigation.navigate('FunChallengesLobby');
              } catch {
                // non-fatal
              }
            }}
          />
        </ScrollView>
      </View>

      {/* Forfeit confirmation (item 6). Solo: this is just ending your own
          game, so the wording drops the "everyone else" framing. */}
      <SlideModal
        visible={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        modalHeaderTitle={isSolo ? 'End game?' : 'Forfeit Puzzle Royale?'}
        type="center"
        contentPadding={20}>
        <View style={styles.confirmBody}>
          <CCText style={styles.confirmText}>
            {isSolo
              ? "You'll end this game and go back to Fun Challenges. You can't rejoin this one."
              : "You'll be marked out and taken back to Fun Challenges. The race continues for everyone else — you can't rejoin this one."}
          </CCText>
          <View style={styles.confirmRow}>
            <Pressable
              onPress={() => setConfirmLeave(false)}
              style={[styles.confirmBtn, styles.confirmBtnSecondary]}>
              <CCText style={styles.confirmBtnSecondaryText}>Keep playing</CCText>
            </Pressable>
            <Pressable
              onPress={handleLeaveRace}
              style={[styles.confirmBtn, styles.confirmBtnPrimary]}>
              <CCText style={styles.confirmBtnPrimaryText}>
                {isSolo ? 'End game' : 'Forfeit'}
              </CCText>
            </Pressable>
          </View>
        </View>
      </SlideModal>

      {/* Full standings — OPT-IN, opened from the board overlay's "View results"
          (#4). The primary end UI is the on-board overlay; this modal is the
          detailed ranked list for those who want it. */}
      <RoyaleResultModal
        visible={isEnded && showStandingsModal}
        me={effectiveSpectator ? undefined : me}
        myRank={effectiveSpectator ? undefined : myRank}
        isSpectator={effectiveSpectator}
        players={displayPlayers}
        leaderboard={displayLeaderboard}
        canRematch={isHost && !classroomContext && !isTournamentRoom}
        onRematch={() => {
          if (session) rematch(session.sessionId);
        }}
        onClose={() => setShowStandingsModal(false)}
      />
    </View>
  );
}

/** Waiting-card settings pill with a leading icon (modern look for the
 *  above-board lobby header). */
/** One label/value line in the Details tab's info block. Mirrors the tournament
 *  lobby's DetailRow, incl. the truncation guard so a long value can't push past
 *  the rail's right edge. */
function DetailInfoRow({label, value}: {label: string; value: string}) {
  return (
    <View style={styles.detailInfoRow}>
      <CCText style={styles.detailInfoLabel}>{label}</CCText>
      <CCText style={styles.detailInfoValue} numberOfLines={1}>
        {value}
      </CCText>
    </View>
  );
}

/** One icon column heading in the leaderboard, with a hover/tap tooltip naming
 *  the column. Uses the repo's Tooltip atom (react-tiny-popover) because
 *  react-native-web drops a bare `title` prop instead of forwarding it. Tapping
 *  works too, so the labels are reachable on touch devices where hover isn't. */
function HeadIconTooltip({
  icon,
  label,
  cellStyle,
}: {
  icon: string;
  label: string;
  cellStyle: any;
}) {
  return (
    <Tooltip text={label}>
      {(toggle: () => void) => (
        <Pressable
          onPress={toggle}
          onHoverIn={toggle}
          onHoverOut={toggle}
          style={[styles.searchHeadIconCell, cellStyle]}>
          <MaterialCommunityIcons name={icon} size={13} color={ARENA_MUTED} />
        </Pressable>
      )}
    </Tooltip>
  );
}

function WaitChip({
  icon,
  text,
  capitalize,
}: {
  icon: string;
  text: string;
  capitalize?: boolean;
}) {
  return (
    <View style={styles.waitChip}>
      <MaterialCommunityIcons name={icon} size={13} color="#2E8B7F" />
      <CCText style={[styles.waitChipText, capitalize && styles.waitChipCap]}>
        {text}
      </CCText>
    </View>
  );
}

/**
 * The 3-tab right rail: Race / Players / Spectators.
 *   - Pre-start (waiting): defaults to Players (roster + host Start +
 *     play/spectate toggle + share link). Race shows the everyone-at-0 strip.
 *   - Post-start: defaults to Race (the live leaderboard strip).
 * Spectators is always available (populated once the spectate path lands).
 */
// Rail panels. 'race'|'players'|'spectators' ship today. FUTURE solo panels
// (personal stats + a daily/weekly solo leaderboard — user's plan, in design)
// would add their key here and a matching config entry + panel in
// RoyaleRightRail; the config-driven railTabList keeps that a one-line change.
type RailTab = 'race' | 'players' | 'spectators' | 'details';
function RoyaleRightRail({
  isSolo,
  soloTimeControl,
  soloHasEndedThisSession,
  soloBestPoints,
  soloHasPb,
  soloCurrent,
  soloBest,
  isWaiting,
  isHost,
  isSpectator,
  isServerSpectator,
  spectating,
  onFollow,
  spectators,
  myUsername,
  shareCode,
  lobby,
  sessionSettings,
  players,
  leaderboard,
  joinOrder,
  myUserId,
  hostIsPlayer,
  onSetHostIsPlayer,
  onStart,
  starting,
  onLeave,
}: {
  // SOLO: no players/spectators tabs — the rail is the race track only, plus the
  // stats header (above) + the leaderboard panel (below the track).
  isSolo: boolean;
  soloTimeControl: number;
  soloHasEndedThisSession: boolean;
  soloBestPoints: number;
  soloHasPb: boolean;
  soloCurrent: {streak: number; points: number; solves: number};
  soloBest: {streak: number; points: number; solves: number};
  isWaiting: boolean;
  isHost: boolean;
  // Read-only spectate styling (server spectator OR a watching classroom coach).
  isSpectator: boolean;
  // TRUE server spectator only (dropped from playerIds). Gates the pre-start
  // read-only "Watching this lobby" branch — a watching coach is a wire-player
  // and keeps full host controls (Play/Watch toggle + Start) before the race.
  isServerSpectator: boolean;
  spectating: string | null;
  onFollow: (userId: string | null) => void;
  spectators: Array<{userId: string; name: string; avatarUrl?: string}>;
  myUsername: string;
  shareCode: string;
  lobby: ReturnType<typeof useFunChallengesRoyaleStore.getState>['lobby'];
  // Details tab falls back to these once the lobby is cleared at start. Typed
  // (not `any`) so a missing/renamed field is a compile error here.
  sessionSettings:
    | ReturnType<
        typeof useFunChallengesRoyaleStore.getState
      >['session']['settings']
    | undefined;
  players: Record<string, PlayerRoyaleState>;
  leaderboard: string[];
  joinOrder: string[];
  myUserId: string;
  hostIsPlayer: boolean;
  onSetHostIsPlayer: (v: boolean) => void;
  onStart: () => void;
  starting: boolean;
  onLeave: () => void;
}) {
  // Spectators land on the Players tab (it's the follow-a-player picker). SOLO
  // has no players/spectators tabs, so it is ALWAYS on the race track.
  const [tab, setTab] = useState<RailTab>(
    isSolo ? 'race' : isWaiting || isSpectator ? 'players' : 'race',
  );
  // Snap the default tab when crossing the start boundary in EITHER direction:
  // → race on start; → players when (re)entering a waiting room, e.g. a rematch
  // drops everyone back into the lobby (item 2: land on the player list). Solo
  // never leaves the race track.
  const prevWaiting = useRef(isWaiting);
  useEffect(() => {
    if (isSolo) return;
    if (prevWaiting.current && !isWaiting) setTab('race');
    else if (!prevWaiting.current && isWaiting) setTab('players');
    prevWaiting.current = isWaiting;
  }, [isWaiting, isSolo]);

  // Details-tab accordion (#2): exactly ONE explainer section open at a time;
  // "How it works" is the default, and opening the scoring ladder collapses it.
  // Starts CLOSED (null) so Details opens compact with the race-info block as
  // the first thing you read; re-tapping an open header collapses it. Matches
  // the tournament lobby's accordion.
  const [detailSection, setDetailSection] = useState<'how' | 'scoring' | null>(
    null,
  );
  const toggleDetailSection = (s: 'how' | 'scoring') =>
    setDetailSection(prev => (prev === s ? null : s));

  // Tick for the lobby expiry countdown (waiting only).
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!isWaiting) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isWaiting]);

  // When the host has chosen to SPECTATE (hostIsPlayer:false), they are a room
  // member but NOT a player — so the waiting-room roster, the count, and the
  // start gate must EXCLUDE them, and they show in the spectators list instead
  // (#1). This mirrors what the server does at start.
  const hostSpectatingInLobby =
    isWaiting && !!lobby && lobby.hostIsPlayer === false && !!lobby.hostId;
  const lobbyPlayers = (lobby?.players ?? []).filter(
    p => !(hostSpectatingInLobby && String(p.userId) === String(lobby!.hostId)),
  );
  // The host (when spectating) is shown in the spectators list even pre-start,
  // before the server's spectator-roster broadcast catches up. Also fill in
  // MY OWN name from the logged-in user if the roster entry lost it (#5).
  const effectiveSpectators = (() => {
    // Backfill blank spectator names: my own row from the logged-in username,
    // and any other row from the players map if that user was also seen there
    // (#2: a spectator whose roster name went missing showed "Spectator").
    const fillSelf = (
      list: Array<{userId: string; name: string; avatarUrl?: string}>,
    ) =>
      list.map(s => {
        if (s.name) {
          return s;
        }
        if (String(s.userId) === String(myUserId) && myUsername) {
          return {...s, name: myUsername};
        }
        const fromPlayers = players[String(s.userId)]?.name;
        if (fromPlayers) {
          return {...s, name: fromPlayers};
        }
        return s;
      });
    if (!hostSpectatingInLobby) {
      return fillSelf(spectators);
    }
    const hostId = String(lobby!.hostId);
    if (spectators.some(s => String(s.userId) === hostId)) {
      return fillSelf(spectators);
    }
    const hostEntry = (lobby!.players || []).find(
      p => String(p.userId) === hostId,
    );
    return fillSelf([
      {userId: hostId, name: hostEntry?.name || '', avatarUrl: hostEntry?.avatarUrl},
      ...spectators,
    ]);
  })();

  const count = isWaiting ? lobbyPlayers.length : leaderboard.length;

  // Rail tab config — declarative so the visible tabs are chosen by CONTEXT and
  // a new panel is a one-entry addition (not a JSX rewrite). SOLO shows the race
  // track only (no Players/Spectators). FUTURE (solo personal-stats + a daily/
  // weekly solo leaderboard panel — user's plan, still in design): add e.g.
  // `{key:'stats', label:'Stats'}` to the solo branch here + a `tab==='stats'`
  // panel below; nothing else changes. See RailTab.
  const railTabList: Array<{key: RailTab; label: string}> = isSolo
    ? [] // race track only; the tab bar is hidden (single panel)
    : [
        {key: 'race', label: 'Race'},
        {key: 'players', label: `Players · ${count}`},
        {
          key: 'spectators',
          label: effectiveSpectators.length
            ? `Spectators · ${effectiveSpectators.length}`
            : 'Spectators',
        },
        // 4th tab (#1) — mirrors the scheduled-tournament lobby so both screens
        // expose the same "what is this / how does it score" panel.
        {key: 'details', label: 'Details'},
      ];

  return (
    <View style={styles.rail}>
      {/* SOLO has no Players / Spectators tabs — the rail is the race track
          only, so the tab bar is hidden entirely (railTabList is empty). */}
      {railTabList.length > 0 && (
        <View style={styles.railTabs}>
          {railTabList.map(t => (
            <Pressable
              key={t.key}
              onPress={() => setTab(t.key)}
              style={[styles.railTab, tab === t.key && styles.railTabActive]}>
              <CCText
                style={[
                  styles.railTabText,
                  tab === t.key && styles.railTabTextActive,
                ]}
                numberOfLines={1}>
                {t.label}
              </CCText>
            </Pressable>
          ))}
        </View>
      )}

      {tab === 'race' && !isSolo && (
        <RoyaleLeaderboardStrip
          players={players}
          leaderboard={leaderboard}
          joinOrder={joinOrder}
          myUserId={myUserId}
          // Spectators can switch who they follow by tapping a race row (#5).
          selectable={isSpectator}
          spectating={spectating}
          onFollow={onFollow}
          // Stretch the race track to the bottom of the rail (#3).
          fill
        />
      )}

      {/* SOLO rail: stats header ABOVE the race track (chase your PB ghost) +
          the leaderboard panel BELOW it. All live in-game. */}
      {tab === 'race' && isSolo && (
        <View style={styles.soloRail}>
          <SoloStatsHeader
            current={soloCurrent}
            best={soloBest}
            hasPb={soloHasPb}
          />
          <RoyaleLeaderboardStrip
            players={players}
            leaderboard={leaderboard}
            joinOrder={joinOrder}
            myUserId={myUserId}
            solo
            soloBestPoints={soloBestPoints}
            soloHasPb={soloHasPb}
          />
          <SoloLeaderboardPanel
            defaultTimeControlSec={soloTimeControl}
            hasEndedThisSession={soloHasEndedThisSession}
          />
        </View>
      )}

      {tab === 'players' && (
        <View style={styles.railBody}>
          {/* Pre-start SPECTATOR: a read-only waiting note + the roster, no host
              controls/share. They auto-transition to the live spectate view on
              start (#2/#3). */}
          {isWaiting && lobby && isServerSpectator && (
            <>
              <View style={styles.waitCard}>
                <CCText style={styles.waitCardTitle}>Watching this lobby</CCText>
                <CCText style={styles.waitCardSub}>
                  You'll start following the race as soon as the host begins.
                </CCText>
              </View>
              <RoyaleLobbyRoster
                players={lobbyPlayers}
                myUserId={myUserId}
                isHost={false}
                expiresAt={lobby.expiresAt}
                nowMs={nowMs}
                onStart={onStart}
                starting={starting}
              />
              {/* Leave button hoisted out to the sticky rail footer (P6.3) so
                  it shows on ALL tabs pre-start, not just this one. */}
            </>
          )}
          {isWaiting && lobby && !isServerSpectator && (
            <>
              <RoyaleShareSheet shareCode={lobby.shareCode} />
              {isHost && (
                <View style={styles.roleRow}>
                  {[
                    {label: 'Play', val: true},
                    {label: 'Watch', val: false},
                  ].map(opt => {
                    const selected = hostIsPlayer === opt.val;
                    return (
                      <Pressable
                        key={opt.label}
                        onPress={() => onSetHostIsPlayer(opt.val)}
                        style={[
                          styles.roleChip,
                          selected && styles.roleChipSelected,
                        ]}>
                        <CCText
                          style={[
                            styles.roleChipText,
                            selected && styles.roleChipTextSelected,
                          ]}>
                          {opt.label}
                        </CCText>
                      </Pressable>
                    );
                  })}
                </View>
              )}
              <RoyaleLobbyRoster
                players={lobbyPlayers}
                myUserId={myUserId}
                isHost={isHost}
                expiresAt={lobby.expiresAt}
                nowMs={nowMs}
                onStart={onStart}
                starting={starting}
              />
              {/* Leave button hoisted to the sticky rail footer (P6.3). */}
            </>
          )}
          {!isWaiting && (
            <PlayerSearchList
              players={players}
              leaderboard={leaderboard}
              myUserId={myUserId}
              selectable={isSpectator}
              spectating={spectating}
              onFollow={onFollow}
            />
          )}
        </View>
      )}

      {tab === 'spectators' && (
        <View style={styles.railBody}>
          {/* Lobby code so anyone can be invited to watch even after the race
              has started (#4). */}
          {!!shareCode && <RoyaleShareSheet shareCode={shareCode} />}
          <View style={styles.searchPanel}>
            <CCText style={styles.searchTitle}>Spectators</CCText>
            {effectiveSpectators.length === 0 ? (
              <CCText style={styles.searchEmpty}>
                No spectators watching yet.
              </CCText>
            ) : (
              <SpectatorList
                spectators={effectiveSpectators}
                myUserId={myUserId}
              />
            )}
          </View>
        </View>
      )}

      {/* ── Details tab (#1) — the same explainer the scheduled-tournament
          lobby shows: the room's settings, how the race works, and the
          collapsible scoring ladder. Available before AND during the race. ── */}
      {tab === 'details' && (
        <View style={styles.railBody}>
          {!!shareCode && <RoyaleShareSheet shareCode={shareCode} />}

          {/* Race INFO — always shown, in-race and post-race alike. Reads from
              `lobby.settings` while the lobby object exists and falls back to
              `sessionSettings` after start (the lobby is cleared then), so this
              no longer disappears the moment the race begins. Label/value rows
              instead of chips, matching the tournament lobby's Details tab.
              NOTE: the session lives in the PARENT's store hook — this is a
              separate component, so it arrives as a prop. Reading `session`
              here is an unbound identifier, not a closure. */}
          {(() => {
            const s: any = lobby?.settings || sessionSettings;
            if (!s) return null;
            return (
              <View style={[styles.searchPanel, styles.detailPanel]}>
                <CCText style={styles.searchTitle}>
                  {s.isTournament ? 'This tournament' : 'This race'}
                </CCText>
                {/* Tournament identity. Post-race this Details tab is the only
                    one left (the scheduled lobby's is gone), so without the name
                    you couldn't tell WHICH tournament you were reviewing.
                    "Created by" is deliberately NOT shown — hosts are staff and
                    players don't need it. */}
                {s.isTournament && !!s.tournamentName && (
                  <DetailInfoRow label="Name" value={String(s.tournamentName)} />
                )}
                {s.isTournament && !!s.scheduledAt && (
                  <DetailInfoRow
                    label="Started"
                    value={new Date(s.scheduledAt).toLocaleString()}
                  />
                )}
                <DetailInfoRow
                  label="Time control"
                  value={`${s.timerSec ?? 90}s`}
                />
                <DetailInfoRow
                  label="Difficulty"
                  value={String(s.difficulty ?? 'easy')}
                />
                <DetailInfoRow
                  label="Max players"
                  value={String(s.maxPlayers ?? 100)}
                />
                <DetailInfoRow
                  label="Topics"
                  value={
                    Array.isArray(s.topics) && s.topics.length
                      ? `${s.topics.length} selected`
                      : 'Any topic'
                  }
                />
              </View>
            );
          })()}

          <View style={[styles.searchPanel, styles.detailPanel]}>
            {/* How it works — collapsible, open by default (#2). */}
            <Pressable
              onPress={() => toggleDetailSection('how')}
              style={styles.detailAccHeader}>
              <CCText style={styles.searchTitle}>How it works</CCText>
              <MaterialCommunityIcons
                name={detailSection === 'how' ? 'chevron-up' : 'chevron-down'}
                size={18}
                color={TEAL_DARK}
              />
            </Pressable>
            {detailSection === 'how' ? (
              <View style={styles.detailRules}>
                {[
                  {
                    icon: 'flag-checkered',
                    t: `Solve as many puzzles as you can before the clock runs out — whoever scores the most points wins.`,
                  },
                  {
                    icon: 'account-group',
                    t: 'Final rank is by total points (ties broken by who reached the score first).',
                  },
                  {
                    icon: 'eye-outline',
                    t: 'Anyone with the join code can watch the race live.',
                  },
                ].map(r => (
                  <View key={r.t} style={styles.detailRuleRow}>
                    <MaterialCommunityIcons
                      name={r.icon}
                      size={16}
                      color={TEAL_DARK}
                    />
                    <CCText style={styles.detailRuleText}>{r.t}</CCText>
                  </View>
                ))}
              </View>
            ) : null}

            {/* Scoring & streaks — collapsible, mutually exclusive (#2). */}
            <Pressable
              onPress={() => toggleDetailSection('scoring')}
              style={styles.detailAccHeader}>
              <CCText style={styles.searchTitle}>Scoring &amp; streaks</CCText>
              <MaterialCommunityIcons
                name={
                  detailSection === 'scoring' ? 'chevron-up' : 'chevron-down'
                }
                size={18}
                color={TEAL_DARK}
              />
            </Pressable>
            {detailSection === 'scoring' ? (
              <View style={styles.detailRules}>
                <CCText style={styles.detailRuleText}>
                  Every correct solve raises your combo by 1 and scores points. A
                  wrong solve resets your combo to 0 (you keep your points). The
                  longer your streak, the more each solve is worth:
                </CCText>
                {[
                  {range: '1–2 in a row', pts: '1'},
                  {range: '3–4 in a row', pts: '2'},
                  {range: '5–9 in a row', pts: '3'},
                  {range: '10–14 in a row', pts: '4'},
                  {range: '15+ in a row', pts: '6'},
                ].map(r => (
                  <View key={r.range} style={styles.detailLadderRow}>
                    <CCText style={styles.detailRuleText}>{r.range}</CCText>
                    <CCText style={styles.detailLadderPts}>{`+${r.pts}`}</CCText>
                  </View>
                ))}
              </View>
            ) : null}
          </View>
        </View>
      )}

      {/* Sticky pre-start leave button (P6.3): below the rail body, visible on
          ALL tabs while waiting (Race / Players / Spectators / Details). NOT
          rendered in solo (no lobby) or once the race is live — in-game leaving
          uses the existing bottom-left forfeit button, so no duplicate. */}
      {isWaiting && !isSolo && (
        <Pressable onPress={onLeave} style={[styles.leaveBtn, styles.leaveBtnSticky]}>
          <CCText style={styles.leaveBtnText}>
            {isServerSpectator ? 'Stop watching' : 'Leave lobby'}
          </CCText>
        </Pressable>
      )}
    </View>
  );
}

/** One leaderboard row, memoized so a standings tick only re-renders the rows
 *  whose (rank/points/combo/eliminated) actually changed — at 100 players an
 *  unmemoized map re-rendered every row on every 300ms delta flush. */
const LeaderboardRow = React.memo(function LeaderboardRow({
  userId,
  name,
  rank,
  points,
  combo,
  solved,
  eliminated,
  isYou,
  selectable,
  isFollowed,
  onFollow,
}: {
  userId: string;
  name: string;
  rank: number;
  points: number;
  combo: number;
  solved: number;
  eliminated: boolean;
  isYou: boolean;
  selectable: boolean;
  isFollowed: boolean;
  onFollow?: (userId: string | null) => void;
}) {
  const RowTag: any = selectable ? Pressable : View;
  return (
    <RowTag
      onPress={selectable && onFollow ? () => onFollow(userId) : undefined}
      style={[
        styles.searchRow,
        // Tint YOUR row (the lobby roster does the same), so you can find
        // yourself without reading names. `followed` still wins over it.
        isYou && styles.searchRowYou,
        isFollowed && styles.searchRowFollowed,
        eliminated && styles.searchRowEliminated,
      ]}>
      <CCText style={styles.searchRank}>#{rank}</CCText>
      <CCText
        style={[styles.searchName, isYou && styles.searchNameYou]}
        numberOfLines={1}>
        {isYou ? 'You' : name || 'Player'}
      </CCText>
      {isFollowed && (
        <View style={styles.followingBadge}>
          <CCText style={styles.followingBadgeText}>WATCHING</CCText>
        </View>
      )}
      {eliminated && (
        <View style={styles.outBadge}>
          <CCText style={styles.outBadgeText}>OUT</CCText>
        </View>
      )}
      {/* Streak: ALWAYS shown as a number (0 included) so the column never has
          holes — a blank cell read as missing data rather than "no streak". The
          tile is only TINTED on an actual streak (>=2); 0/1 render on a plain
          muted background so the yellow still signals something notable. */}
      <View style={styles.comboTileCol}>
        <View
          style={[
            styles.comboTile,
            !(combo >= 2 && !eliminated) && styles.comboTileIdle,
          ]}>
          <CCText
            style={[
              styles.comboTileText,
              !(combo >= 2 && !eliminated) && styles.comboTileTextIdle,
            ]}>
            {eliminated ? 0 : combo}
          </CCText>
        </View>
      </View>
      <CCText style={styles.searchSolved}>{solved}</CCText>
      <CCText style={styles.searchPts}>{points}</CCText>
    </RowTag>
  );
});

const LEADERBOARD_PAGE_SIZE = 10;
/** Spectator rows per page — the app-wide convention. */
const SPECTATOR_PAGE_SIZE = 10;

/**
 * Spectator roster — searchable + paginated.
 *
 * Spectators are capped NOWHERE (unlike players, bounded by max_players), so a
 * popular race can accumulate more watchers than racers. This list previously
 * mapped all of them into the rail with no slice and no max-height.
 *
 * Uses the shared app pager rather than the local `‹ Prev / Next ›` control
 * PlayerSearchList uses, matching the tournaments tab / video courses / meetings.
 */
function SpectatorList({
  spectators,
  myUserId,
}: {
  spectators: Array<{userId: string; name?: string}>;
  myUserId: string;
}) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const nameOf = useCallback(
    (s: {userId: string; name?: string}) =>
      s.userId === myUserId ? 'You' : s.name || 'Spectator',
    [myUserId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return spectators;
    return spectators.filter(s => nameOf(s).toLowerCase().includes(q));
  }, [spectators, query, nameOf]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / SPECTATOR_PAGE_SIZE),
  );
  useEffect(() => {
    setPage(1);
  }, [query]);
  // Spectators leave mid-race, so the list can shrink under the current page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = filtered.slice(
    (page - 1) * SPECTATOR_PAGE_SIZE,
    page * SPECTATOR_PAGE_SIZE,
  );

  return (
    <>
      {spectators.length > SPECTATOR_PAGE_SIZE ? (
        <CCTextInput
          value={query}
          onChangeText={setQuery}
          placeholder="Search spectators"
        />
      ) : null}
      <View style={styles.searchList}>
        {pageRows.length === 0 ? (
          <CCText style={styles.searchEmpty}>No spectators match.</CCText>
        ) : null}
        {pageRows.map(s => {
          const isMe = s.userId === myUserId;
          return (
            <View key={s.userId} style={styles.searchRow}>
              <View style={styles.spectatorDot} />
              <CCText
                style={[styles.searchName, isMe && styles.searchNameYou]}
                numberOfLines={1}>
                {nameOf(s)}
              </CCText>
            </View>
          );
        })}
      </View>
      {totalPages > 1 ? (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          paginate={setPage}
          maxPage={7}
        />
      ) : null}
    </>
  );
}

/**
 * Live leaderboard — points-DESC, searchable, paginated (P6.2). Rows are
 * ordered by the authoritative store `leaderboard` (already points-ranked
 * server-side), NOT by name; each carries rank/points/solved/combo/eliminated.
 * Paginated in pages of LEADERBOARD_PAGE_SIZE so a 100-player race renders a
 * bounded number of rows at a time (the previous version mapped ALL players,
 * name-sorted, re-rendering every row per delta). Kept inside the rail
 * ScrollView, so pagination uses page buttons rather than a nested
 * VirtualizedList (avoids the RN-web nesting warning). When `selectable`
 * (spectator) rows follow that player's board.
 *
 * NOTE `page` here is ZERO-based (it predates the shared pager); the shared
 * Pagination component is ONE-based, so the two are converted at the boundary
 * where it is rendered rather than renumbering this component's internals.
 */
function PlayerSearchList({
  players,
  leaderboard,
  myUserId,
  selectable = false,
  spectating = null,
  onFollow,
}: {
  players: Record<string, PlayerRoyaleState>;
  leaderboard: string[];
  myUserId: string;
  selectable?: boolean;
  spectating?: string | null;
  onFollow?: (userId: string | null) => void;
}) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);

  // Ordered rows: leaderboard order first (points-DESC, authoritative), then
  // any players not yet in the leaderboard array appended by name. Memoized on
  // the store slices so this only recomputes when standings actually move.
  const ordered = useMemo(() => {
    const seen = new Set<string>();
    const rows: Array<{p: PlayerRoyaleState; rank: number}> = [];
    leaderboard.forEach((id, i) => {
      const p = players[id];
      if (p) {
        rows.push({p, rank: i + 1});
        seen.add(id);
      }
    });
    const rest = Object.values(players)
      .filter(p => !seen.has(p.userId))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    rest.forEach(p => rows.push({p, rank: rows.length + 1}));
    return rows;
  }, [players, leaderboard]);

  const filtered = useMemo(() => {
    if (!query) return ordered;
    const q = query.toLowerCase();
    return ordered.filter(r => (r.p.name || '').toLowerCase().includes(q));
  }, [ordered, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / LEADERBOARD_PAGE_SIZE));
  // Clamp the page if the list shrank (or a search narrowed it).
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * LEADERBOARD_PAGE_SIZE;
  const pageRows = filtered.slice(start, start + LEADERBOARD_PAGE_SIZE);

  const onSearch = useCallback((t: string) => {
    setQuery(t);
    setPage(0); // a new search always resets to the first page of matches
  }, []);

  return (
    <View style={styles.searchPanel}>
      <CCText style={styles.searchTitle}>
        {selectable ? 'Follow a player' : 'Leaderboard'}
      </CCText>
      <CCTextInput
        value={query}
        onChangeText={onSearch}
        placeholder="Search players"
      />
      {/* Column headings. The rows show three bare numbers (combo / solved /
          points) and nobody could tell which was which — the race track has a
          header row, so this mirrors it. Widths match the row cells exactly. */}
      <View style={styles.searchHead}>
        <CCText style={[styles.searchHeadCell, styles.searchHeadRank]}>#</CCText>
        <CCText style={[styles.searchHeadCell, {flex: 1}]}>Player</CCText>
        {/* ICONS, not words. The columns are only 40px wide, so "Streak" and
            "Solved" either wrapped ("STR/EAK") or had to be truncated — both
            looked bad. These are the same glyphs the end-of-race stat strip
            uses, and each is wrapped in the repo's Tooltip atom so the meaning
            stays discoverable on hover / tap. A plain `title` prop does NOT
            work: react-native-web forwards only an allowlisted set of props to
            the DOM and `title` isn't one, so the earlier version showed nothing. */}
        <HeadIconTooltip
          icon="flash"
          label="Streak — consecutive correct solves"
          cellStyle={styles.searchHeadCombo}
        />
        <HeadIconTooltip
          icon="check-circle-outline"
          label="Puzzles solved"
          cellStyle={styles.searchHeadSolved}
        />
        <HeadIconTooltip
          icon="star-four-points"
          label="Points — solves plus streak bonuses"
          cellStyle={styles.searchHeadPts}
        />
      </View>
      <View style={styles.searchList}>
        {pageRows.map(({p, rank}) => (
          <LeaderboardRow
            key={p.userId}
            userId={p.userId}
            name={p.name || ''}
            rank={rank}
            points={p.points}
            combo={p.combo}
            solved={p.solved ?? 0}
            eliminated={!!p.eliminated}
            isYou={p.userId === myUserId}
            selectable={selectable}
            isFollowed={selectable && spectating === p.userId}
            onFollow={onFollow}
          />
        ))}
        {filtered.length === 0 && (
          <CCText style={styles.searchEmpty}>No players match.</CCText>
        )}
      </View>

      {/* Pager — only when more than one page. Uses the shared app pager so the
          in-race standings match the tournaments tab / results table; `page` is
          zero-based here, so convert on the way in and out. */}
      {pageCount > 1 && (
        <View style={styles.pager}>
          <Pagination
            currentPage={safePage + 1}
            totalPages={pageCount}
            paginate={(p: number) => setPage(p - 1)}
            maxPage={7}
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {flex: 1, minHeight: 0, backgroundColor: BG_PAGE},
  // Error banner (e.g. sequence-fetch timeout on Start) — sits above the layout
  // so the host sees it + a Retry instead of a silently-stuck Start button.
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginHorizontal: 20,
    marginTop: 12,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: 'rgba(205,44,57,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(205,44,57,0.25)',
  },
  errorBannerText: {
    flex: 1,
    fontSize: FontSizes[13] || 13,
    fontWeight: '600',
    color: '#A3242E',
  },
  errorBannerActions: {flexDirection: 'row', alignItems: 'center', gap: 8},
  errorRetryBtn: {
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 8,
    backgroundColor: '#3DAB9E',
  },
  errorRetryText: {fontSize: FontSizes[13] || 13, fontWeight: '800', color: '#fff'},
  errorDismissBtn: {paddingHorizontal: 10, paddingVertical: 7},
  errorDismissText: {
    fontSize: FontSizes[12] || 12,
    fontWeight: '700',
    color: '#A3242E',
    textDecorationLine: 'underline',
  },
  // Fills the screen height so the right-rail ScrollView gets a bounded height
  // to scroll within (board column stays its natural size, top-aligned).
  scrollContent: {padding: 20, flex: 1, minHeight: 0},
  emptyRoot: {
    flex: 1,
    backgroundColor: BG_PAGE,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  emptyText: {
    fontSize: FontSizes[15] || 15,
    color: TEXT_MUTED,
    fontWeight: '600',
  },
  layout: {gap: 16},
  // 'stretch' so the right-rail ScrollView inherits the row's (screen) height
  // and can scroll internally instead of growing the page.
  layoutWide: {flexDirection: 'row', alignItems: 'stretch', flex: 1, minHeight: 0},
  layoutNarrow: {flexDirection: 'column', flex: 1, minHeight: 0},
  // UI-2: left grid panel 15% · board center 70% · race track 15% when wide.
  // (leftCol kept for the waiting/legacy paths; new live layout uses gridCol +
  // centerCol.)
  leftCol: {gap: 12},
  leftColWide: {flexGrow: 6, flexShrink: 1, flexBasis: 0, minWidth: 0},
  // Raised zIndex so the grid's hover-preview board floats OVER the board
  // column (a later sibling) instead of behind it (#1).
  gridCol: {gap: 8, zIndex: 1000},
  gridColWide: {flexGrow: 15, flexShrink: 1, flexBasis: 0, minWidth: 170},
  centerCol: {gap: 12, alignItems: 'center'},
  // In-play race: board ~50%. Waiting/ended: board wider (old proportions).
  centerColRace: {flexGrow: 50, flexShrink: 1, flexBasis: 0, minWidth: 0},
  centerColWide: {flexGrow: 60, flexShrink: 1, flexBasis: 0, minWidth: 0},
  // minHeight:0 so the rail's own ScrollView engages instead of the column
  // growing the row (a flex child's default min-height:auto refuses to shrink
  // below content). Without it, expanding a Details accordion scrolled the whole
  // page instead of just the rail. Mirrors RoyaleLobbyShell's rightCol.
  rightCol: {gap: 12, minHeight: 0},
  // In-play race: race track ~35%. Waiting/ended: the old 40% rail so the lobby
  // roster / share panel / standings have room (#1, #5).
  rightColRace: {flexGrow: 35, flexShrink: 1, flexBasis: 0, minWidth: 220},
  rightColWide: {flexGrow: 40, flexShrink: 1, flexBasis: 0, minWidth: 260},
  // flexGrow so the race-track card (fill) can stretch to the bottom (#3).
  rightColContent: {gap: 12, paddingBottom: 24, flexGrow: 1},
  // Who-to-play (colour box + label) on the left, clock pill on the right.
  whoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignSelf: 'center',
  },
  whoLeft: {flexDirection: 'row', alignItems: 'center', gap: 8},
  whoColorBox: {
    width: 22,
    height: 22,
    borderRadius: 5,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.25)',
  },
  whoColorBoxBlack: {backgroundColor: '#2B2B2B', borderColor: '#2B2B2B'},
  whoText: {fontSize: FontSizes[16] || 16, fontWeight: '800', color: BROWN},
  // Tip under the board, in a white card (#1).
  boardTipCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: CREAM3,
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginTop: 4,
    alignSelf: 'center',
  },
  boardTip: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '600',
    color: TEAL_DARK,
    textAlign: 'center',
  },
  // Post-game review board layered over the (still-mounted) game board.
  reviewOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: '#FAF6EB',
    zIndex: 20,
  },
  // Below-board result card wrapper (replaces the hint once the overlay is
  // dismissed post-game).
  belowResultWrap: {marginTop: 4, alignSelf: 'center', width: '100%'},
  // Spectator "Watching <name> · Stop" header above the followed grid.
  watchingRow: {flexDirection: 'row', alignItems: 'center', gap: 6},
  watchingDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    backgroundColor: '#CD2C39',
  },
  watchingText: {
    flex: 1,
    fontSize: FontSizes[13] || 13,
    fontWeight: '800',
    color: BROWN,
  },
  stopChip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: 'rgba(205,44,57,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(205,44,57,0.30)',
  },
  stopChipText: {fontSize: FontSizes[12] || 12, fontWeight: '800', color: '#CD2C39'},
  boardWrap: {position: 'relative', alignSelf: 'center'},
  // Pre-start: real starting-position board + caption below it.
  waitingBoardWrap: {gap: 12, alignItems: 'center'},
  waitingBoardText: {
    fontSize: FontSizes[15] || 15,
    fontWeight: '700',
    color: TEAL_DARK,
    textAlign: 'center',
  },
  // Waiting-room scoreboard (replaces the clock pre-start).
  waitCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: CREAM3,
    paddingHorizontal: 18,
    paddingVertical: 14,
  },
  waitCardTitle: {fontSize: FontSizes[18] || 18, fontWeight: '800', color: BROWN},
  waitCardSub: {
    fontSize: FontSizes[13] || 13,
    color: TEXT_MUTED,
    marginTop: 4,
    textTransform: 'capitalize',
  },
  // Iconified settings chips under the waiting-card title.
  waitChips: {flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10},
  waitChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(61, 171, 158, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(61, 171, 158, 0.30)',
  },
  waitChipText: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '700',
    color: '#2E8B7F',
  },
  waitChipCap: {textTransform: 'capitalize'},
  // 3-tab right rail.
  // flex:1 so the race-track card (fill) stretches to the bottom of the rail
  // regardless of player count — the right side reads as fully filled (#2).
  rail: {gap: 12, flex: 1},
  railTabs: {
    flexDirection: 'row',
    gap: 4,
    backgroundColor: '#F4EEDB',
    borderRadius: 10,
    padding: 4,
  },
  railTab: {
    flex: 1,
    paddingVertical: 9,
    borderRadius: 7,
    alignItems: 'center',
  },
  railTabActive: {backgroundColor: TEAL},
  railTabText: {
    fontSize: FontSizes[12] || 12,
    fontWeight: '700',
    color: TEXT_MUTED,
  },
  railTabTextActive: {color: '#fff'},
  // flexGrow so the active tab's panel can stretch to the bottom of the rail
  // (the race track already did; Players/Spectators left dead cream space below
  // a short list). The rail's ScrollView contentContainer is flexGrow:1 too.
  railBody: {gap: 12, flexGrow: 1, minHeight: 0},
  soloRail: {gap: 14},
  roleRow: {flexDirection: 'row', gap: 8},
  roleChip: {
    flex: 1,
    paddingVertical: 9,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#2623221A',
    backgroundColor: '#fff',
    alignItems: 'center',
  },
  roleChipSelected: {backgroundColor: TEAL, borderColor: TEAL},
  roleChipText: {fontSize: FontSizes[13] || 13, fontWeight: '600', color: TEXT_MUTED},
  roleChipTextSelected: {color: '#fff', fontWeight: '700'},
  leaveBtn: {alignItems: 'center', paddingVertical: 10},
  // Sticky rail footer (P6.3): a divider + a little breathing room so the
  // always-visible pre-start leave button reads as separate from the tab body.
  leaveBtnSticky: {
    marginTop: 6,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(77,63,55,0.10)',
  },
  leaveBtnText: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '700',
    color: TEXT_MUTED,
    textDecorationLine: 'underline',
  },
  leaveRaceBtn: {
    alignSelf: 'center',
    paddingVertical: 9,
    paddingHorizontal: 18,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#2623221A',
    backgroundColor: '#fff',
  },
  leaveRaceText: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '700',
    color: TEXT_MUTED,
  },
  confirmBody: {gap: 16},
  confirmText: {
    fontSize: FontSizes[15] || 15,
    color: TEXT_DARK,
    lineHeight: 22,
  },
  confirmRow: {flexDirection: 'row', gap: 12},
  confirmBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  confirmBtnSecondary: {
    borderWidth: 1,
    borderColor: '#2623221A',
    backgroundColor: '#fff',
  },
  confirmBtnSecondaryText: {
    fontSize: FontSizes[14] || 14,
    fontWeight: '700',
    color: TEXT_MUTED,
  },
  confirmBtnPrimary: {backgroundColor: '#CD2C39'},
  confirmBtnPrimaryText: {
    fontSize: FontSizes[14] || 14,
    fontWeight: '800',
    color: '#fff',
  },
  countdownOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(250,246,235,0.7)',
    borderRadius: 8,
  },
  // End result overlaid on the board (#4) — dim the board, centre the card.
  endOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(38,35,34,0.55)',
    borderRadius: 8,
    padding: 12,
    zIndex: 30,
  },
  countdownPill: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: CREAM3,
    paddingHorizontal: 40,
    paddingVertical: 22,
    minWidth: 160,
  },
  countdownLabel: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '700',
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 2,
  },
  countdownDigit: {
    fontSize: 84,
    fontWeight: '800',
    color: BROWN,
    lineHeight: 92,
  },
  countdownGo: {
    fontSize: 72,
    fontWeight: '800',
    color: TEAL_DARK,
    lineHeight: 80,
  },
  finalizingText: {
    fontSize: FontSizes[18] || 18,
    fontWeight: '800',
    color: BROWN,
  },
  // search panel
  // Arena-style panel: white card, generous radius, hairline warm border and a
  // very soft lifted shadow — same treatment as the tournament lobby's tabCard,
  // so Players / Spectators / Details look identical in both places. (The Race
  // tab is deliberately untouched.)
  searchPanel: {
    backgroundColor: '#fff',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ARENA_LINE,
    padding: 16,
    gap: 10,
    // Fill the rail's height so the card bottoms out level with the race track's
    // panel instead of floating above dead cream space.
    flex: 1,
    minHeight: 0,
    shadowColor: '#2A2317',
    shadowOpacity: 0.05,
    shadowRadius: 12,
    shadowOffset: {width: 0, height: 3},
    elevation: 2,
  },
  searchTitle: {
    fontSize: FontSizes[11] || 11,
    fontWeight: '800',
    color: ARENA_MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  searchList: {
    gap: 6,
    // flex:1 so the list (and therefore the white card) stretches to the BOTTOM
    // of the rail, matching the race track's full-height panel. The old fixed
    // `maxHeight: 460` left dead cream space under the card with only 2 players.
    // overflow keeps any rows beyond the visible area INSIDE the card (they
    // scroll) instead of painting outside it — a plain View with only maxHeight
    // let RN-for-Web render children past the box, which was the "spilling over
    // the white background" bug seen with 9 players.
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    overflowY: 'auto' as any,
  },
  // Arena-parity rows: taller, rounder, on a faint warm surface with a hairline
  // border so each player reads as its own card (matches the tournament lobby's
  // roster rows). Race tab is untouched.
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 11,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: '#FBF8F1',
    borderWidth: 1,
    borderColor: ARENA_LINE,
  },
  searchRank: {
    width: 32,
    fontSize: FontSizes[12] || 12,
    fontWeight: '700',
    color: 'rgba(77,63,55,0.4)',
  },
  searchName: {
    flex: 1,
    fontSize: FontSizes[14] || 14,
    fontWeight: '600',
    color: TEXT_DARK,
  },
  searchNameYou: {color: TEAL_DARK, fontWeight: '800'},
  searchRowYou: {
    backgroundColor: 'rgba(42,157,143,0.08)',
    borderColor: 'rgba(42,157,143,0.22)',
  },
  searchRowFollowed: {
    backgroundColor: 'rgba(61,171,158,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(61,171,158,0.30)',
  },
  followingBadge: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: TEAL,
  },
  followingBadgeText: {
    fontSize: 9,
    fontWeight: '800',
    color: '#fff',
    letterSpacing: 0.5,
  },
  // Yellow combo tile — matches RoyaleStrip's filled pip (#8).
  comboTile: {
    width: 24,
    height: 24,
    borderRadius: 6,
    backgroundColor: '#FACF47',
    alignItems: 'center',
    justifyContent: 'center',
  },
  comboTileText: {fontSize: 12, fontWeight: '800', color: '#4D3F37'},
  // No active streak (0 or 1): same footprint, muted instead of yellow.
  comboTileIdle: {backgroundColor: 'rgba(77,63,55,0.07)'},
  comboTileTextIdle: {color: TEXT_MUTED},
  // 40px column centring the 24px tile, so the tile sits under the "Streak"
  // heading (which needs 40px for the full word).
  comboTileCol: {width: 40, alignItems: 'center'},
  spectatorDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    backgroundColor: '#8A7E5F',
  },
  searchPts: {
    // Fixed width + centred so the value sits under its centred icon heading.
    width: 30,
    textAlign: 'center',
    fontSize: FontSizes[14] || 14,
    fontWeight: '800',
    color: BROWN,
  },
  // Column headings above the rows (mirrors the race track's header row).
  // Cell widths MUST match the row cells: rank 32, combo tile 24, solved 26,
  // pts 30, with the row's 10px gap.
  searchHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingBottom: 2,
  },
  searchHeadCell: {
    fontSize: FontSizes[10] || 10,
    fontWeight: '800',
    color: ARENA_MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  searchHeadRank: {width: 32},
  // Widths MUST stay in sync with comboTileCol / searchSolved / searchPts.
  searchHeadCombo: {width: 40},
  searchHeadSolved: {width: 40},
  searchHeadPts: {width: 30},
  // Icon heading cell — centres the glyph over its (right-aligned) number
  // column. `alignItems:'flex-end'` for the two right-aligned columns would sit
  // the icon hard against the edge, so centre reads better at this width.
  searchHeadIconCell: {alignItems: 'center', justifyContent: 'center'},
  searchEmpty: {
    fontSize: FontSizes[13] || 13,
    color: TEXT_MUTED,
    paddingVertical: 8,
  },
  // Details tab info rows (label ↔ value), matching the tournament lobby.
  detailInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 7,
    borderTopWidth: 1,
    borderTopColor: ARENA_LINE,
  },
  detailInfoLabel: {
    fontSize: FontSizes[12] || 12,
    color: ARENA_MUTED,
    flexShrink: 0,
  },
  detailInfoValue: {
    fontSize: FontSizes[12] || 12,
    fontWeight: '700',
    color: TEXT_DARK,
    textTransform: 'capitalize',
    flexShrink: 1,
    textAlign: 'right',
  },
  // Details tab (#1): collapsible explainer sections + the scoring ladder.
  detailAccHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 2,
  },
  // Details-tab panels size to their CONTENT, unlike `searchPanel` (which is
  // `flex: 1` so the Players list fills the rail). Sharing `searchPanel` here
  // pinned both explainer cards to a stretched height, so expanding the scoring
  // ladder overflowed the card and the last row ("15+ in a row") spilled past
  // the rounded bottom edge. `flex: 0` + auto height lets the card grow with the
  // accordion; the rail's own ScrollView handles any overflow.
  // `flex: 0` is NOT right here — it expands to `flexBasis: 0%`, which collapsed
  // the card to zero height and let its rows overlap the card below. Spell the
  // three parts out instead: never grow, never shrink, size to CONTENT.
  detailPanel: {flexGrow: 0, flexShrink: 0, flexBasis: 'auto'},
  detailRules: {gap: 8, paddingBottom: 4},
  detailRuleRow: {flexDirection: 'row', alignItems: 'flex-start', gap: 8},
  detailRuleText: {
    flex: 1,
    fontSize: FontSizes[12] || 12,
    color: TEXT_MUTED,
    lineHeight: 18,
  },
  detailLadderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    paddingVertical: 3,
    borderTopWidth: 1,
    borderTopColor: 'rgba(77,63,55,0.08)',
  },
  detailLadderPts: {
    fontSize: FontSizes[12] || 12,
    fontWeight: '700',
    color: TEAL_DARK,
  },
  // Solved count column (between name/combo and points) — P6.2 adds solved
  // alongside score so the standings read like the final leaderboard.
  searchSolved: {
    // 40 wide, CENTRED so the value sits under its centred icon heading.
    width: 40,
    textAlign: 'center',
    fontSize: FontSizes[12] || 12,
    fontWeight: '700',
    color: 'rgba(77,63,55,0.55)',
  },
  searchRowEliminated: {opacity: 0.55},
  outBadge: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: 'rgba(205,44,57,0.14)',
  },
  outBadgeText: {
    fontSize: 9,
    fontWeight: '800',
    color: '#A3242E',
    letterSpacing: 0.5,
  },
  // Pager wrapper — the shared Pagination component draws the controls; this
  // just centres them under the list.
  pager: {
    alignItems: 'center',
    marginTop: 4,
  },
});
